/**
 * ====================================================================
 * FIKO DÖKÜM KONTROL — SAHNE İÇİ HUD
 * ====================================================================
 *
 * Döküm (casting) kontrol analizini sahne üzerinde gösteren overlay HUD.
 * - "Kontrol Et" ile motor analizi başlatır (ince cidar, sıcak nokta,
 *   ani kesit, hapsolan hava göstergeleri).
 * - Sonuç bölgeleri için sahnede CALLOUT etiketleri (AnnotationManager,
 *   mevcut değilse HTML overlay fallback).
 * - Yolluk (gating/feeding) elemanları için renkli 3D işaretçiler;
 *   öneriler hayalet (yarı saydam), tıklanınca kabul edilir.
 * - Isı haritası: bölge vertex'leri vertex-color ile renklendirilir.
 *
 * Orkestratör (FikoDokumKontrol) IDokumKontrolMotor sözleşmesine göre
 * tüketilir — somut sınıf başka bir ajan tarafından yazılıyor.
 */

import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, computed, signal, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

import { FikoYTKokBilesenComponent } from '../../../../../../kok-bilesen/kok-bilesen.component';
import { FikoDokumKontrol } from './FikoDokumKontrol';
import { FikoYollukDuzenleyici } from './FikoYollukDuzenleyici';
import {
  AkisProfili,
  BesleyiciTipi,
  SogutucuMalzeme,
  EtiketModu,
  GostergeTipi,
  GOSTERGE_BASLIK,
  GOSTERGE_RENK,
  IDokumBolge,
  IDokumKontrolMotor,
  IGostergeAyar,
  IDolumSimSonuc,
  IKontrolSonuc,
  IMethodingSonuc,
  IYollukEleman,
  IYollukGuncelleme,
  YOLLUK_BASLIK,
  YOLLUK_RENK,
  YollukTipi,
} from './IDokumKontrol';
import { IDolumSonuc, voxelIndeks } from './simulasyon/IDokumSim';
import { FikoDolumGorsel } from './simulasyon/FikoDolumGorsel';
import { IKatilasmaSonuc } from './simulasyon/IKatilasmaSim';
import { getDokumMalzemeDB, DokumAlasimKategori, KalipMalzemeKategori } from '../fiko-sph-sivi/FikoDokumMalzemeDB';

/** 3D düzenleyici transform modu */
type TransformMod = 'sec' | 'tasi' | 'dondur';

/** Alaşım/kum seçici için basit liste öğesi */
interface IMalzemeSecenek { id: string; ad: string; }

const ETIKET_MODU_ANAHTAR = 'dokum-etiket-modu';
const GIRIS_YON_MODU_ANAHTAR = 'dokum-giris-yon-modu';
const ALASIM_ANAHTAR = 'dokum-alasim';
const KUM_ANAHTAR = 'dokum-kum';
const YUKARI_YON_ANAHTAR = 'dokum-yukari-yon';

/** Döküm/yerçekimi yön seçeneği — yukari: yerçekiminin TERSİ (metal aşağı akar) */
interface IYonSecenek { deger: string; etiket: string; baslik: string; yukari: [number, number, number]; }

/** Dolum girişi yön belirleme modu */
type GirisYonModu = 'otomatik' | 'iki_tik' | 'surukle';

/** Sahneye gömülü tek bölge etiketi (CSS2DObject + işaret + lider çizgi) */
interface ISahneEtiket {
  bolgeId: string;
  obj: CSS2DObject;
  div: HTMLDivElement;
  isaret: THREE.Mesh;
  cizgi: THREE.Line;
}

@Component({
  selector: 'fiko-dokum-kontrol',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="dk-hud" [class.dk-tikla-modu]="tiklamaModu() || yon3dSecModu()">

      <!-- ───────── Sol üst: Not rozeti ───────── -->
      @if (sonuc(); as s) {
        <div class="dk-not" [style.--dk-not-renk]="notRenk()">
          <div class="dk-not-harf">{{ s.not.not }}</div>
          <div class="dk-not-bilgi">
            <div class="dk-not-sayilar">
              <span class="dk-uyari">{{ s.not.toplamUyari }} uyarı</span>
              <span class="dk-ayrac">·</span>
              <span class="dk-kritik">{{ s.not.kritikSayi }} kritik</span>
            </div>
            <div class="dk-not-ozet" [title]="s.not.ozet">{{ s.not.ozet }}</div>
          </div>
        </div>
      }

      <!-- Kapat -->
      <button class="dk-kapat-ana" title="Kapat (Esc)" (click)="kapat.emit()">✕</button>

      <!-- ───────── Sağ üst: Araç çubuğu ───────── -->
      <div class="dk-toolbar">
        <button class="dk-btn dk-btn-ana" (click)="kontrolEt()" [disabled]="calisiyor()">
          @if (calisiyor() && simAsama() === null) { <span class="dk-spin"></span> Analiz… {{ ilerleme() }}% }
          @else { ⚙️ Kontrol Et }
        </button>

        <button class="dk-btn" (click)="yollukOner()" [disabled]="!sonuc() || calisiyor()">
          ✨ Yolluk Öner
        </button>

        <button class="dk-btn" (click)="otomatikDuzelt()" [disabled]="!sonuc() || calisiyor()"
                title="Göstergelere göre yolluk/besleyici/hava deliği otomatik yerleştir ve kabul et">
          🤖 Otomatik Düzelt
        </button>

        <button class="dk-btn" (click)="dolumSimBaslat()" [disabled]="!sonuc() || calisiyor()"
                [class.aktif]="dolumAktif()" title="Dolum simülasyonu — kaviteyi voxelleyip akışı animasyonla göster">
          @if (simAsama() === 'dolum') { <span class="dk-spin"></span> Dolum… {{ ilerleme() }}% }
          @else { 💧 Dolum Simülasyonu }
        </button>

        <button class="dk-btn" (click)="katilasmaSimBaslat()" [disabled]="!sonuc() || calisiyor()"
                [class.aktif]="katilasmaAktif()" title="Katılaşma simülasyonu — geç katılaşan bölgeler, çekme ve sıcak yırtılma riski">
          @if (simAsama() === 'katilasma') { <span class="dk-spin"></span> Katılaşma… {{ ilerleme() }}% }
          @else { ❄ Katılaşma }
        </button>

        <button class="dk-btn" (click)="raporToggle()" [disabled]="!sonuc()"
                [class.aktif]="raporAcik()" title="Kusur raporu — tüm tespitleri özetle">
          📋 Rapor
        </button>

        <button class="dk-btn" (click)="gorselAyarToggle()"
                [class.aktif]="gorselAyarAcik()" title="Görsel ayarlar — voxel boyutu, renk, çözünürlük">
          ⚙ Görsel
        </button>

        <button class="dk-btn" (click)="varyantKarsilastir()" [disabled]="!sonuc() || calisiyor() || varyantCalisiyor()"
                title="Proses penceresi — döküm sıcaklığını tarayıp cold-shut/misrun karşılaştır">
          @if (varyantCalisiyor()) { <span class="dk-spin"></span> Tarama… }
          @else { 📊 Proses }
        </button>

        <div class="dk-ayrac-dik"></div>

        <!-- Manuel yolluk tip seçici -->
        <div class="dk-yolluk-secici">
          @for (t of yollukTipleri; track t) {
            <button class="dk-yolluk-tip"
                    [class.aktif]="aktifYollukTip() === t"
                    [style.--dk-y-renk]="yollukRenk(t)"
                    [title]="yollukBaslik(t)"
                    (click)="yollukTipSec(t)">
              {{ yollukSimge(t) }}
            </button>
          }
        </div>

        <!-- Dolum girişi yön modu (sadece dolum_girisi aktifken) -->
        @if (aktifYollukTip() === 'dolum_girisi') {
          <div class="dk-ayrac-dik"></div>
          <div class="dk-yon-modu">
            @for (ym of girisYonModlari; track ym.deger) {
              <button class="dk-mod"
                      [class.aktif]="girisYonModu() === ym.deger"
                      [title]="ym.baslik"
                      (click)="girisYonModuSec(ym.deger)">
                {{ ym.etiket }}
              </button>
            }
          </div>
        }

        <!-- Transform mod toggle (Seç/Taşı/Döndür) — en az 1 yolluk varken -->
        @if (yolluklar().length > 0) {
          <div class="dk-ayrac-dik"></div>
          <div class="dk-transform-modu">
            @for (tm of transformModlari; track tm.deger) {
              <button class="dk-mod"
                      [class.aktif]="transformMod() === tm.deger"
                      [title]="tm.baslik"
                      (click)="transformModuSec(tm.deger)">
                {{ tm.simge }}
              </button>
            }
          </div>
        }

        <div class="dk-ayrac-dik"></div>

        <!-- Etiket görünüm modu -->
        <div class="dk-etiket-modu">
          @for (m of etiketModlari; track m.deger) {
            <button class="dk-mod"
                    [class.aktif]="etiketModu() === m.deger"
                    [title]="m.baslik"
                    (click)="etiketModuSec(m.deger)">
              {{ m.simge }}
            </button>
          }
        </div>

        <div class="dk-ayrac-dik"></div>

        <!-- Döküm / yerçekimi yön seçici -->
        <div class="dk-yon-secici" [title]="'Döküm akış yönü — ' + yukariYonEtiket()">
          <span class="dk-yon-ok">⬆</span>
          <div class="dk-yon-butonlar">
            @for (s of yonSecenekleri; track s.deger) {
              <button class="dk-mod dk-yon-btn"
                      [class.aktif]="yukariYon() === s.deger"
                      [title]="s.baslik"
                      (click)="yukariYonSec(s)">
                {{ s.deger }}
              </button>
            }
            <button class="dk-mod dk-yon-btn dk-yon-3d"
                    [class.aktif]="yon3dSecModu()"
                    title="3D'de seç: parçada üst olacak yüzeye tıkla"
                    (click)="yon3dToggle()">🎯 3D</button>
            <button class="dk-mod dk-yon-btn"
                    title="Yönü tersine çevir (parçayı baş aşağı dök)"
                    (click)="yonTersineCevir()">⇅</button>
          </div>
          <span class="dk-yon-etiket">{{ yukariYonEtiket() }}</span>
        </div>

        <div class="dk-ayrac-dik"></div>

        <!-- Alaşım & kum seçici -->
        <div class="dk-malzeme-secici">
          <label class="dk-ms-grup" title="Döküm alaşımı">
            <span class="dk-ms-et">Alaşım</span>
            <select class="dk-ms-select" [ngModel]="alasimId()" (ngModelChange)="alasimSec($event)">
              @for (a of alasimListesi; track a.id) {
                <option [value]="a.id">{{ a.ad }}</option>
              }
            </select>
          </label>
          <label class="dk-ms-grup" title="Kalıp kumu">
            <span class="dk-ms-et">Kum</span>
            <select class="dk-ms-select" [ngModel]="kumId()" (ngModelChange)="kumSec($event)">
              @for (k of kumListesi; track k.id) {
                <option [value]="k.id">{{ k.ad }}</option>
              }
            </select>
          </label>
        </div>
      </div>

      <!-- ───────── Tıklama modu ipucu ───────── -->
      @if (tiklamaModu(); as tm) {
        <div class="dk-ipucu">
          @if (girisDuzenlemeAktif()) {
            <span class="dk-ipucu-renk" [style.background]="yollukRenk(tm)"></span>
            <span>Akış yönünü ayarlayın</span>
            <button class="dk-ipucu-btn dk-onayla" (click)="girisOnayla()">✓ Onayla</button>
            <button class="dk-ipucu-btn" (click)="girisIptal()">İptal</button>
          } @else {
            <span class="dk-ipucu-renk" [style.background]="yollukRenk(tm)"></span>
            <span>{{ yollukBaslik(tm) }} için parçaya tıklayın</span>
            <button class="dk-ipucu-btn dk-onayla" (click)="yerlestirmeyiBitir()">✓ Bitir</button>
          }
        </div>
      }

      <!-- ───────── Seçili bölge detay kartı ───────── -->
      @if (seciliBolge(); as b) {
        <div class="dk-detay" [style.--dk-d-renk]="bolgeRenk(b)">
          <div class="dk-detay-bas">
            <span class="dk-detay-tip">{{ b.baslik }}</span>
            <button class="dk-detay-kapat" (click)="detayKapat()">✕</button>
          </div>
          <div class="dk-detay-deger">{{ b.deger | number:'1.0-2' }} {{ b.birim }}</div>
          <p class="dk-detay-aciklama">{{ b.aciklama }}</p>
          @if (b.cozuldu) {
            <p class="dk-detay-cozum">✓ {{ b.cozumMesaji || 'Yollukla çözüldü.' }}</p>
          } @else if (b.oneri) {
            <p class="dk-detay-oneri">💡 {{ b.oneri }}</p>
          }
        </div>
      }

      <!-- ───────── Döküm Hesabı (methoding) paneli ───────── -->
      @if (methodingSonuc(); as m) {
        <div class="dk-methoding">
          <div class="dk-mh-bas">
            <span class="dk-mh-baslik">🧮 Döküm Hesabı</span>
            <span class="dk-mh-malzeme">{{ m.alasimAd }} · {{ m.kumAd }}</span>
          </div>

          <div class="dk-mh-izgara">
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Parça ağırlığı</span>
              <span class="dk-mh-deg">{{ m.parcaKutleKg | number:'1.0-2' }} kg</span>
              <span class="dk-mh-alt">{{ m.parcaHacimCm3 | number:'1.0-2' }} cm³</span>
            </div>
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Döküm sıcaklığı</span>
              <span class="dk-mh-deg">{{ m.dokumSicaklikMinC | number:'1.0-0' }}–{{ m.dokumSicaklikMaxC | number:'1.0-0' }} °C</span>
            </div>
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Çekme</span>
              <span class="dk-mh-deg">%{{ m.cekmeYuzde | number:'1.0-2' }}</span>
            </div>
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Yoğunluk</span>
              <span class="dk-mh-deg">{{ m.yogunlukGcm3 | number:'1.0-2' }} g/cm³</span>
            </div>
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Toplam debi</span>
              <span class="dk-mh-deg">{{ m.toplamDebiGramSn | number:'1.0-2' }} g/s</span>
            </div>
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Dolum süresi</span>
              <span class="dk-mh-deg">{{ m.dolumSuresiSn | number:'1.0-2' }} s</span>
            </div>
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Giriş hızı</span>
              <span class="dk-mh-deg" [style.color]="m.girisHiziUygun ? '#22c55e' : '#ef4444'">
                {{ m.girisHiziMs | number:'1.0-2' }} m/s
              </span>
              <span class="dk-mh-alt">limit {{ m.girisHiziLimitMs | number:'1.0-2' }}</span>
            </div>
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Verim</span>
              <span class="dk-mh-deg">%{{ m.verimYuzde | number:'1.0-2' }}</span>
            </div>
            <div class="dk-mh-oge">
              <span class="dk-mh-et">Toplam dökülen</span>
              <span class="dk-mh-deg">{{ m.toplamDokulenKg | number:'1.0-2' }} kg</span>
            </div>
          </div>

          @if (m.besleyiciModul.length > 0) {
            <div class="dk-mh-bolum">
              <div class="dk-mh-bolum-bas">Besleyici modül</div>
              @for (k of m.besleyiciModul; track k.id) {
                <div class="dk-mh-satir" [class.gecersiz]="!k.yeterli">
                  <span class="dk-mh-isaret" [style.color]="k.yeterli ? '#22c55e' : '#ef4444'">{{ k.yeterli ? '✓' : '✗' }}</span>
                  <span class="dk-mh-mesaj">{{ k.mesaj }}</span>
                </div>
              }
            </div>
          }

          @if (m.uyarilar.length > 0) {
            <div class="dk-mh-bolum">
              <div class="dk-mh-bolum-bas">Uyarılar</div>
              @for (u of m.uyarilar; track u) {
                <div class="dk-mh-satir dk-mh-uyari">
                  <span class="dk-mh-nokta"></span>
                  <span class="dk-mh-mesaj">{{ u }}</span>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- ───────── Kusur Raporu paneli ───────── -->
      @if (raporAcik() && sonuc(); as s) {
        <div class="dk-rapor">
          <div class="dk-rapor-bas">
            <span>📋 Kusur Raporu</span>
            <div class="dk-rapor-arac">
              <button class="dk-rapor-btn" (click)="raporKopyala()" title="Panoya kopyala">📄 Kopyala</button>
              <button class="dk-detay-kapat" (click)="raporAcik.set(false)">✕</button>
            </div>
          </div>
          <div class="dk-rapor-icerik">
            @if (castabilite(); as cb) {
              <div class="dk-cast" [style.--dk-cast-renk]="castabilitePuanRenk(cb.puan)">
                <div class="dk-cast-skor">
                  <span class="dk-cast-puan">{{ cb.puan }}</span>
                  <span class="dk-cast-yuz">/100</span>
                </div>
                <div class="dk-cast-bilgi">
                  <div class="dk-cast-baslik">Dökülebilirlik</div>
                  <div class="dk-cast-seviye">{{ cb.seviye }}</div>
                </div>
              </div>
              @if (cb.oneriler.length) {
                <div class="dk-rapor-bolum">
                  <div class="dk-rapor-bolum-bas">💡 Öneriler ({{ cb.oneriler.length }})</div>
                  @for (o of cb.oneriler; track o.metin) {
                    <div class="dk-cast-oneri" [class.yuksek]="o.oncelik === 'yüksek'" [class.orta]="o.oncelik === 'orta'">
                      <span class="dk-cast-rozet">{{ o.oncelik }}</span>{{ o.metin }}
                    </div>
                  }
                </div>
              }
            }
            @for (bol of rapor(); track bol.baslik) {
              <div class="dk-rapor-bolum">
                <div class="dk-rapor-bolum-bas">{{ bol.baslik }}</div>
                @for (sat of bol.satirlar; track sat) {
                  <div class="dk-rapor-satir" [class.iyi]="sat.startsWith('✓')" [class.kotu]="sat.startsWith('⚠')">{{ sat }}</div>
                }
              </div>
            }
          </div>
        </div>
      }

      <!-- ───────── Proses penceresi (DOE) paneli ───────── -->
      @if (varyantSonuc(); as vs) {
        <div class="dk-varyant">
          <div class="dk-ga-bas">
            <span>📊 Proses Penceresi</span>
            <button class="dk-detay-kapat" (click)="varyantSonuc.set(null)">✕</button>
          </div>
          <div class="dk-varyant-not">Döküm sıcaklığı taraması — cold-shut + misrun</div>
          <table class="dk-varyant-tablo">
            <tr><th>Sıcaklık</th><th>Cold-shut</th><th>Misrun</th></tr>
            @for (v of vs; track v.tempC) {
              <tr [class.iyi]="v.iyi">
                <td>{{ v.tempC }}°C @if (v.iyi) { <span class="dk-v-en">✓ en iyi</span> }</td>
                <td>{{ v.coldShut }}</td>
                <td>{{ v.misrun }}</td>
              </tr>
            }
          </table>
        </div>
      }

      <!-- ───────── Görsel Ayarlar paneli ───────── -->
      @if (gorselAyarAcik()) {
        <div class="dk-gorsel-ayar">
          <div class="dk-ga-bas">
            <span>⚙ Ayarlar</span>
            <button class="dk-detay-kapat" (click)="gorselAyarAcik.set(false)">✕</button>
          </div>

          <div class="dk-ga-bolum-bas">🎨 Görsel</div>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Voxel boyutu <b>{{ voxelBoyut() | number:'1.2-2' }}</b></span>
            <input type="range" min="0.3" max="1" step="0.02"
                   [ngModel]="voxelBoyut()" (ngModelChange)="voxelBoyutDegisti($event)" />
          </label>

          <label class="dk-slider-satir">
            <span class="dk-slider-et">Parlaklık <b>{{ voxelParlaklik() | number:'1.1-1' }}×</b></span>
            <input type="range" min="0.4" max="2.5" step="0.05"
                   [ngModel]="voxelParlaklik()" (ngModelChange)="voxelParlaklikDegisti($event)" />
          </label>

          <div class="dk-profil-satir">
            <span class="dk-slider-et">Akkor parıltı (glow)</span>
            <div class="dk-profil-butonlar">
              <button class="dk-mod" [class.aktif]="glowAcik()" (click)="glowAyarla(true)">Açık</button>
              <button class="dk-mod" [class.aktif]="!glowAcik()" (click)="glowAyarla(false)">Kapalı</button>
            </div>
          </div>

          <div class="dk-profil-satir">
            <span class="dk-slider-et">Pürüzsüz yüzey <small>(sıvımsı)</small></span>
            <div class="dk-profil-butonlar">
              <button class="dk-mod" [class.aktif]="pruzsuzAcik()" (click)="pruzsuzAyarla(true)">Yüzey</button>
              <button class="dk-mod" [class.aktif]="!pruzsuzAcik()" (click)="pruzsuzAyarla(false)">Voxel</button>
            </div>
          </div>
          @if (pruzsuzAcik()) {
            <label class="dk-slider-satir">
              <span class="dk-slider-et">Yüzey kalınlığı <b>{{ pruzsuzKalinlik() | number:'1.0-0' }}</b> <small>(ince parçayı doldurur)</small></span>
              <input type="range" min="1" max="4" step="1"
                     [ngModel]="pruzsuzKalinlik()" (ngModelChange)="pruzsuzKalinlikDegisti($event)" />
            </label>
          }

          <label class="dk-slider-satir">
            <span class="dk-slider-et">
              Çözünürlük <b>{{ voxelCozunurluk() > 0 ? voxelCozunurluk() : 'otomatik' }}</b>
            </span>
            <input type="range" min="0" max="300" step="10"
                   [ngModel]="voxelCozunurluk()" (ngModelChange)="voxelCozunurlukDegisti($event)" />
          </label>
          <div class="dk-ga-not">Çözünürlük değişince <b>Dolum/Katılaşma</b>'ya tekrar bas (yeniden voxellenir). Yüksek = ince voxel, daha ağır.</div>

          <div class="dk-profil-satir">
            <span class="dk-slider-et">Çözüm kalitesi</span>
            <div class="dk-profil-butonlar">
              <button class="dk-mod" [class.aktif]="cozumKalite() === 'onizleme'" (click)="cozumKaliteSec('onizleme')">Önizleme</button>
              <button class="dk-mod" [class.aktif]="cozumKalite() === 'final'" (click)="cozumKaliteSec('final')">Final (SPH GPU)</button>
            </div>
          </div>
          @if (cozumKalite() === 'final') {
            <div class="dk-ga-not">Final: WebGPU parçacık (SPH) serbest-yüzey dolumu — daha doğru, daha yavaş. WebGPU yoksa otomatik Önizleme'ye döner.</div>
          }

          <div class="dk-ga-bolme"></div>
          <div class="dk-profil-satir">
            <span class="dk-slider-et">Kesit (içini gör)</span>
            <div class="dk-profil-butonlar">
              @for (ke of kesitEksenleri; track ke.deger) {
                <button class="dk-mod" [class.aktif]="kesitEksen() === ke.deger" (click)="kesitEksenSec(ke.deger)">{{ ke.etiket }}</button>
              }
            </div>
          </div>
          @if (kesitEksen() !== 'kapali') {
            <label class="dk-slider-satir">
              <span class="dk-slider-et">Kesit konumu <b>%{{ (kesitKonum() * 100) | number:'1.0-0' }}</b></span>
              <input type="range" min="0" max="1" step="0.02"
                     [ngModel]="kesitKonum()" (ngModelChange)="kesitKonumDegisti($event)" />
            </label>
          }

          <div class="dk-ga-bolum-bas">🔬 Analiz Hassasiyeti</div>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Min cidar <b>{{ ayarMinCidar() > 0 ? (ayarMinCidar() + ' mm') : 'otomatik' }}</b></span>
            <input type="range" min="0" max="10" step="0.5"
                   [ngModel]="ayarMinCidar()" (ngModelChange)="ayarMinCidar.set($event)" (change)="analizAyarDegisti()" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Sıcak nokta eşiği <b>{{ ayarSicakModul() | number:'1.1-1' }} cm</b> <small>(↑ az tespit)</small></span>
            <input type="range" min="0.3" max="3" step="0.1"
                   [ngModel]="ayarSicakModul()" (ngModelChange)="ayarSicakModul.set($event)" (change)="analizAyarDegisti()" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Ani kesit oranı <b>{{ ayarKesitOran() | number:'1.1-1' }}×</b></span>
            <input type="range" min="1.5" max="6" step="0.1"
                   [ngModel]="ayarKesitOran()" (ngModelChange)="ayarKesitOran.set($event)" (change)="analizAyarDegisti()" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Min bölge <b>{{ ayarMinBolge() | number:'1.0-0' }}</b> <small>(↑ gürültü azalır)</small></span>
            <input type="range" min="3" max="40" step="1"
                   [ngModel]="ayarMinBolge()" (ngModelChange)="ayarMinBolge.set($event)" (change)="analizAyarDegisti()" />
          </label>
          <div class="dk-ga-not">Analiz ayarı değişince otomatik yeniden Kontrol Et çalışır.</div>

          <div class="dk-ga-bolum-bas">🧩 Yolluk Önerisi</div>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Max besleyici <b>{{ ayarMaxBesleyici() | number:'1.0-0' }}</b></span>
            <input type="range" min="1" max="15" step="1"
                   [ngModel]="ayarMaxBesleyici()" (ngModelChange)="ayarMaxBesleyici.set($event)" (change)="yollukAyarUygula()" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Besleyici max çap <b>{{ ayarBesleyiciCap() | number:'1.0-0' }} mm</b></span>
            <input type="range" min="15" max="120" step="5"
                   [ngModel]="ayarBesleyiciCap()" (ngModelChange)="ayarBesleyiciCap.set($event)" (change)="yollukAyarUygula()" />
          </label>
          <div class="dk-ga-not">Değişince <b>Otomatik Düzelt</b> / <b>Yolluk Öner</b>'e tekrar bas.</div>

          <div class="dk-ga-bolum-bas">🔥 Döküm Prosesi</div>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Döküm sıcaklığı <b>{{ ayarDokumSicaklik() > 0 ? (ayarDokumSicaklik() + '°C') : 'otomatik' }}</b></span>
            <input type="range" min="0" max="1500" step="10"
                   [ngModel]="ayarDokumSicaklik()" (ngModelChange)="ayarDokumSicaklik.set($event)" (change)="prosesAyarUygula()" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Kalıp ön ısıtma <b>{{ ayarKalipSicaklik() | number:'1.0-0' }}°C</b></span>
            <input type="range" min="20" max="400" step="5"
                   [ngModel]="ayarKalipSicaklik()" (ngModelChange)="ayarKalipSicaklik.set($event)" (change)="prosesAyarUygula()" />
          </label>
          <div class="dk-ga-not">Sıcak döküm = az cold-shut, çok gaz/çekme. Kalıp ön ısıtma = yavaş katılaşma. Değişince Dolum/Katılaşma'ya tekrar bas.</div>

          <div class="dk-ga-bolum-bas">🧪 Malzeme (Parametrik)
            @if (mlzAktifMi()) { <button class="dk-mod" style="margin-left:auto" (click)="mlzSifirla()">↺ DB'ye Dön</button> }
          </div>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">T likidüs <b>{{ mlzDeger('T_liquidus') | number:'1.0-0' }}°C</b></span>
            <input type="range" min="300" max="1600" step="1"
                   [ngModel]="mlzDeger('T_liquidus')" (ngModelChange)="mlzDegistir('T_liquidus', $event)" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">T solidüs <b>{{ mlzDeger('T_solidus') | number:'1.0-0' }}°C</b></span>
            <input type="range" min="250" max="1550" step="1"
                   [ngModel]="mlzDeger('T_solidus')" (ngModelChange)="mlzDegistir('T_solidus', $event)" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Gizli ısı <b>{{ (mlzDeger('gizliIsi') / 1000) | number:'1.0-0' }} kJ/kg</b></span>
            <input type="range" min="50000" max="600000" step="1000"
                   [ngModel]="mlzDeger('gizliIsi')" (ngModelChange)="mlzDegistir('gizliIsi', $event)" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Çekme oranı β <b>%{{ (mlzDeger('cekmeOrani') * 100) | number:'1.1-1' }}</b></span>
            <input type="range" min="0" max="0.12" step="0.005"
                   [ngModel]="mlzDeger('cekmeOrani')" (ngModelChange)="mlzDegistir('cekmeOrani', $event)" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Akışkanlık <b>{{ mlzDeger('akiskanlikIndeksi') | number:'1.0-0' }}/100</b></span>
            <input type="range" min="20" max="100" step="1"
                   [ngModel]="mlzDeger('akiskanlikIndeksi')" (ngModelChange)="mlzDegistir('akiskanlikIndeksi', $event)" />
          </label>
          <label class="dk-slider-satir">
            <span class="dk-slider-et">Yoğunluk (katı) <b>{{ mlzDeger('yogunlukKati') | number:'1.0-0' }} kg/m³</b></span>
            <input type="range" min="1500" max="9500" step="10"
                   [ngModel]="mlzDeger('yogunlukKati')" (ngModelChange)="mlzDegistir('yogunlukKati', $event)" />
          </label>
          <div class="dk-ga-not">DB alaşımının üstüne yazar (what-if). Değişince Dolum/Katılaşma'ya tekrar bas.</div>

          <div class="dk-ga-bolum-bas">💾 Senaryo</div>
          <div class="dk-profil-butonlar">
            <button class="dk-mod" (click)="senaryoKaydet()">⬇ Kaydet (JSON)</button>
            <button class="dk-mod" (click)="senaryoDosyaSecici.click()">⬆ Yükle</button>
            <input #senaryoDosyaSecici type="file" accept=".json,application/json" style="display:none" (change)="senaryoYukle($event)" />
          </div>
          <div class="dk-ga-not">Yolluklar + zamanlama + malzeme + proses tek dosyada — what-if senaryolarını sakla, paylaş, karşılaştır.</div>
        </div>
      }

      <!-- ───────── Sol: Yolluk listesi paneli ───────── -->
      @if (yolluklar().length > 0) {
        <div class="dk-yolluk-panel">
          <div class="dk-yp-bas">🧩 Yolluklar <span class="dk-yp-say">{{ yolluklar().length }}</span></div>
          <div class="dk-yp-liste">
            @for (y of yolluklar(); track y.id) {
              <div class="dk-yp-satir"
                   [class.secili]="seciliYolluk()?.id === y.id"
                   [style.--dk-y-renk]="yollukRenk(y.tip)"
                   (click)="yollukSec(y)">
                <span class="dk-yp-simge">{{ yollukSimge(y.tip) }}</span>
                <span class="dk-yp-ad">{{ yollukBaslik(y.tip) }}</span>
                @if (!y.kabulEdildi) { <span class="dk-yp-hayalet" title="Öneri (kabul edilmedi)">◌</span> }
                <span class="dk-yp-durum" [class.gecersiz]="!y.gecerli" [title]="y.dogrulamaMesaji">{{ y.gecerli ? '✓' : '✗' }}</span>
                <button class="dk-yp-sil" title="Sil" (click)="yollukSilTikla(y, $event)">🗑</button>
              </div>
            }
          </div>
        </div>
      }

      <!-- ───────── Özellik paneli (seçili yolluk) ───────── -->
      @if (seciliYolluk(); as y) {
        <div class="dk-ozellik" [style.--dk-o-renk]="yollukRenk(y.tip)">
          <div class="dk-ozellik-bas">
            <span class="dk-ozellik-tip">{{ yollukSimge(y.tip) }} {{ yollukBaslik(y.tip) }}</span>
            <button class="dk-detay-kapat" (click)="yollukSecimBirak()">✕</button>
          </div>

          <label class="dk-slider-satir">
            <span class="dk-slider-et">Çap <b>{{ y.capMm | number:'1.0-0' }} mm</b></span>
            <input type="range" min="5" max="60" step="1"
                   [ngModel]="y.capMm"
                   (ngModelChange)="capDegistir(y, $event)" />
          </label>

          <label class="dk-slider-satir">
            <span class="dk-slider-et">Yükseklik <b>{{ y.yukseklikMm | number:'1.0-0' }} mm</b></span>
            <input type="range" min="5" max="120" step="1"
                   [ngModel]="y.yukseklikMm"
                   (ngModelChange)="yukseklikDegistir(y, $event)" />
          </label>

          @if (y.tip === 'dolum_girisi') {
            <label class="dk-slider-satir">
              <span class="dk-slider-et">Debi <b>{{ (y.debiGramSn || 0) | number:'1.0-0' }} g/s</b></span>
              <input type="range" min="5" max="2000" step="5"
                     [ngModel]="y.debiGramSn || 100"
                     (ngModelChange)="debiDegistir(y, $event)" />
            </label>
            <div class="dk-profil-satir">
              <span class="dk-slider-et">Akış profili</span>
              <div class="dk-profil-butonlar">
                @for (p of akisProfilleri; track p.deger) {
                  <button class="dk-mod"
                          [class.aktif]="(y.akisProfili || 'sabit') === p.deger"
                          [title]="p.baslik"
                          (click)="akisProfiliDegistir(y, p.deger)">{{ p.etiket }}</button>
                }
              </div>
            </div>
            <label class="dk-slider-satir">
              <span class="dk-slider-et">Açılma <b>{{ (y.acilmaSn || 0) | number:'1.0-1' }} s</b></span>
              <input type="range" min="0" max="60" step="0.5"
                     [ngModel]="y.acilmaSn || 0"
                     (ngModelChange)="acilmaDegistir(y, $event)" />
            </label>
            <label class="dk-slider-satir">
              <span class="dk-slider-et">Kapanma <b>{{ y.kapanmaSn ? ((y.kapanmaSn | number:'1.0-1') + ' s') : 'hiç' }}</b></span>
              <input type="range" min="0" max="120" step="0.5"
                     [ngModel]="y.kapanmaSn || 0"
                     (ngModelChange)="kapanmaDegistir(y, $event)" />
            </label>
            <div class="dk-ga-not">Zamanlı döküm: bu giriş {{ (y.acilmaSn || 0) | number:'1.0-1' }}. saniyede akmaya başlar{{ y.kapanmaSn ? (', ' + (y.kapanmaSn | number:'1.0-1') + '. saniyede kapanır') : '' }}.</div>
          }

          @if (y.tip === 'besleyici') {
            <div class="dk-profil-satir">
              <span class="dk-slider-et">Besleyici tipi</span>
              <div class="dk-profil-butonlar">
                @for (b of besleyiciTipleri; track b.deger) {
                  <button class="dk-mod"
                          [class.aktif]="(y.besleyiciTipi || 'acik') === b.deger"
                          [title]="b.baslik"
                          (click)="besleyiciTipiDegistir(y, b.deger)">{{ b.etiket }}</button>
                }
              </div>
            </div>
            <div class="dk-ga-not">Ekzotermik/yalıtımlı gömlek besleyiciyi geç katılaştırır → daha uzun besler, pipe besleyicide kalır.</div>
          }

          @if (y.tip === 'sogutucu') {
            <div class="dk-profil-satir">
              <span class="dk-slider-et">Soğutucu malzemesi</span>
              <div class="dk-profil-butonlar">
                @for (s of sogutucuMalzemeleri; track s.deger) {
                  <button class="dk-mod"
                          [class.aktif]="(y.sogutucuMalzeme || 'celik') === s.deger"
                          [title]="s.baslik"
                          (click)="sogutucuMalzemeDegistir(y, s.deger)">{{ s.etiket }}</button>
                }
              </div>
            </div>
            <div class="dk-ga-not">Bakır en hızlı ısı çeker (12×), grafit 8×, çelik 6× (kuma göre).</div>
          }

          @if (y.tip === 'sensor') {
            <div class="dk-sensor-okuma">
              @if (y.sensorVeri?.dolumSn != null || y.sensorVeri?.katilasmaSn != null || y.sensorVeri?.kalinlikMm != null) {
                @if (y.sensorVeri?.dolumSn != null) { <div class="dk-sensor-satir"><span>💧 Dolum süresi</span><b>{{ y.sensorVeri!.dolumSn | number:'1.0-2' }} s</b></div> }
                @if (y.sensorVeri?.katilasmaSn != null) { <div class="dk-sensor-satir"><span>❄ Katılaşma</span><b>{{ y.sensorVeri!.katilasmaSn | number:'1.0-2' }} s</b></div> }
                @if (y.sensorVeri?.kalinlikMm != null) { <div class="dk-sensor-satir"><span>📏 Kalınlık</span><b>{{ y.sensorVeri!.kalinlikMm | number:'1.0-1' }} mm</b></div> }
              } @else {
                <div class="dk-sensor-bos">Dolum/Katılaşma çalıştırınca ölçüm gelir.</div>
              }
              @if (sogumaEgrisiVar(y.id)) {
                <div class="dk-soguma-baslik">🌡 Soğuma eğrisi (T–t)</div>
                <svg class="dk-soguma-svg" viewBox="0 0 120 40" preserveAspectRatio="none">
                  <path [attr.d]="sogumaEgrisiPath(y.id)" fill="none" stroke="#e11d8f" stroke-width="1.5" />
                </svg>
              }
            </div>
          }

          <div class="dk-ozellik-yon">
            Yön: <code>{{ yonMetni(y) }}</code>
            <span class="dk-ozellik-ipucu">Taşı/Döndür ile 3D'de ayarla</span>
          </div>

          <div class="dk-ozellik-dogrulama" [class.gecersiz]="!y.gecerli">
            {{ y.gecerli ? '✓ ' : '✗ ' }}{{ y.dogrulamaMesaji }}
          </div>

          <div class="dk-ozellik-butonlar">
            @if (!y.kabulEdildi) {
              <button class="dk-btn dk-btn-ana" (click)="yollukKabulEt(y)">✓ Kabul Et</button>
            }
            <button class="dk-btn dk-btn-sil" (click)="yollukSilTikla(y)">🗑 Sil</button>
          </div>
        </div>
      }

      <!-- ───────── Dolum simülasyonu zaman çizelgesi ───────── -->
      @if (dolumAktif()) {
        <div class="dk-dolum">
          <div class="dk-dolum-ust">
            <button class="dk-dolum-oynat" [title]="dolumOynuyor() ? 'Duraklat' : 'Oynat'"
                    (click)="dolumOynuyor() ? dolumDuraklat() : dolumOynat()">
              {{ dolumOynuyor() ? '⏸' : '▶' }}
            </button>
            <input class="dk-dolum-slider" type="range" min="0" [max]="dolumSure()" step="0.01"
                   [ngModel]="dolumZaman()" (ngModelChange)="dolumZamanAyarla($event)" />
            <span class="dk-dolum-sure">{{ dolumZaman() | number:'1.1-1' }}s / {{ dolumSure() | number:'1.1-1' }}s</span>
            <button class="dk-dolum-kapat" title="Kapat" (click)="dolumTemizle()">✕</button>
          </div>

          @if (dolumSonuc(); as ds) {
            <div class="dk-dolum-ozet">
              @if (ds.havaCepleri.length > 0) {
                <span class="dk-dolum-rozet dk-r-hava" title="Hapsolan hava cepleri">
                  🫧 {{ ds.havaCepleri.length }} hava cebi
                </span>
              }
              @if (ds.sogukBolgeler.length > 0) {
                <span class="dk-dolum-rozet dk-r-soguk" title="Soğuk birleşme riski">
                  ❄ {{ ds.sogukBolgeler.length }} soğuk bölge
                </span>
              }
              @if (ds.dolmayanSayi > 0) {
                <span class="dk-dolum-rozet dk-r-eksik" title="Hiç dolmayan hücreler">
                  ⚠ Eksik dolum riski
                </span>
              }
              @if (ds.havaCepleri.length === 0 && ds.sogukBolgeler.length === 0 && ds.dolmayanSayi === 0) {
                <span class="dk-dolum-rozet dk-r-temiz">✓ Sorun bulunamadı</span>
              }
            </div>
          }
        </div>
      }

      <!-- ───────── Sıcaklık renk skalası (heat-map legend) ───────── -->
      @if (katilasmaSicaklik(); as ks) {
        <div class="dk-legend-isi">
          <div class="dk-li-baslik">🌡 Sıcaklık</div>
          <div class="dk-li-cubuk">
            <span class="dk-li-deg dk-li-ust">{{ ks.max | number:'1.0-0' }}°C</span>
            <span class="dk-li-deg dk-li-orta">{{ ((ks.max + ks.min) / 2) | number:'1.0-0' }}°C</span>
            <span class="dk-li-deg dk-li-alt">{{ ks.min | number:'1.0-0' }}°C</span>
          </div>
          <div class="dk-li-not">sıvı → katı</div>
        </div>
      }

      <!-- ───────── Katılaşma simülasyonu zaman çizelgesi ───────── -->
      @if (katilasmaAktif()) {
        <div class="dk-dolum dk-katilasma">
          <div class="dk-dolum-ust">
            <button class="dk-dolum-oynat dk-kat-oynat" [title]="katilasmaOynuyor() ? 'Duraklat' : 'Oynat'"
                    (click)="katilasmaOynuyor() ? katilasmaDuraklat() : katilasmaOynat()">
              {{ katilasmaOynuyor() ? '⏸' : '▶' }}
            </button>
            <input class="dk-dolum-slider dk-kat-slider" type="range" min="0" [max]="katilasmaSure()" step="0.01"
                   [ngModel]="katilasmaZaman()" (ngModelChange)="katilasmaZamanAyarla($event)" />
            <span class="dk-dolum-sure">{{ katilasmaZaman() | number:'1.1-1' }}s / {{ katilasmaSure() | number:'1.1-1' }}s</span>
            <button class="dk-dolum-kapat" title="Kapat" (click)="katilasmaTemizle()">✕</button>
          </div>

          <div class="dk-kat-bilgi">Geç katılaşan = sıcak/kırmızı (çekme riski)</div>

          @if (katilasmaSonuc(); as ks) {
            <div class="dk-dolum-ozet">
              @if (ks.cekmeBolgeleri.length > 0) {
                <span class="dk-dolum-rozet dk-r-cekme" title="Niyama çekme gözenekliliği bölgeleri">
                  🔴 {{ ks.cekmeBolgeleri.length }} çekme
                </span>
              }
              @if (katilasmaBeslenmeyenSayi() > 0) {
                <span class="dk-dolum-rozet dk-r-eksik" title="Beslenmemiş sıcak noktalar (çekme adayı)">
                  ⚠ {{ katilasmaBeslenmeyenSayi() }} beslenmemiş sıcak nokta
                </span>
              }
              @if (katilasmaBeslenenSayi() > 0) {
                <span class="dk-dolum-rozet dk-r-sicak" title="Beslenen sıcak noktalar">
                  🟠 {{ katilasmaBeslenenSayi() }} sıcak nokta
                </span>
              }
              @if (ks.yirtilmaBolgeleri.length > 0) {
                <span class="dk-dolum-rozet dk-r-yirtilma" title="Sıcak yırtılma riskli bölgeler">
                  🩹 {{ ks.yirtilmaBolgeleri.length }} sıcak yırtılma
                </span>
              }
              @if (ks.cekmeBolgeleri.length === 0 && ks.sicakNoktalar.length === 0 && ks.yirtilmaBolgeleri.length === 0) {
                <span class="dk-dolum-rozet dk-r-temiz">✓ Kusur bulunamadı</span>
              }
              <!-- Çekme & besleme raporu (pipe + parça boşluğu) -->
              @if (ks.parcaCekmeCm3 != null && ks.parcaCekmeCm3 > 0.01) {
                <span class="dk-dolum-rozet dk-r-cekme" title="Beslenemeyen çekme boşluğu parçada kaldı — besleyici yetersiz">
                  🕳 Parçada {{ ks.parcaCekmeCm3 | number:'1.1-1' }} cm³ çekme boşluğu
                </span>
              } @else if (ks.besleyiciCekmeCm3 != null && ks.besleyiciCekmeCm3 > 0) {
                <span class="dk-dolum-rozet dk-r-temiz" title="Çekmenin tamamı besleyicilere alındı (pipe)">
                  ✓ Çekme besleyicide ({{ ks.besleyiciCekmeCm3 | number:'1.1-1' }} cm³)
                </span>
              }
              @for (br of (ks.besleyiciRapor ?? []); track br.id) {
                <span class="dk-dolum-rozet" [class.dk-r-temiz]="br.yeterli" [class.dk-r-eksik]="!br.yeterli"
                      title="Besleyici hacminin çekme telafisinde kullanılan yüzdesi (pipe derinliği)">
                  ⭕ %{{ br.kullanilanYuzde | number:'1.0-0' }} {{ br.yeterli ? 'yeterli' : 'YETERSİZ' }}
                </span>
              }
              @if (ks.maxGerilmeMpa != null && ks.maxGerilmeMpa > 0) {
                <span class="dk-dolum-rozet dk-r-sicak" title="Termoelastik artık gerilim tahmini (tepe) + çarpılma">
                  🧲 ~{{ ks.maxGerilmeMpa | number:'1.0-0' }} MPa · {{ ks.distorsiyonMm | number:'1.2-2' }} mm çarpılma
                </span>
              }
              @if (ks.macaGazBolgeleri?.length) {
                <span class="dk-dolum-rozet dk-r-hava" title="Maça bağlayıcı gazı blowhole riski — maça çevresi geç katılaşıyor">
                  🫧 {{ ks.macaGazBolgeleri!.length }} maça gazı riski
                </span>
              }
            </div>

            @if (ks.uyarilar.length > 0) {
              <div class="dk-kat-uyarilar">
                @for (u of ks.uyarilar; track u) {
                  <div class="dk-kat-uyari-satir"><span class="dk-mh-nokta"></span><span>{{ u }}</span></div>
                }
              </div>
            }
          }
        </div>
      }

      <!-- ───────── Alt: Legend ───────── -->
      <div class="dk-legend">
        @for (g of gostergeTipleri; track g) {
          <div class="dk-legend-oge">
            <span class="dk-legend-renk" [style.background]="gostergeRenk(g)"></span>
            <span class="dk-legend-ad">{{ gostergeBaslik(g) }}</span>
            <span class="dk-legend-say">{{ tipSayisi(g) }}</span>
          </div>
        }
      </div>

    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 9000;
      font-family: 'Inter', system-ui, sans-serif;
      --dk-cam: rgba(20, 24, 33, 0.72);
      --dk-cam-acik: rgba(255, 255, 255, 0.82);
      --dk-metin: var(--py-metin, #e8edf5);
      --dk-kenar: rgba(255, 255, 255, 0.12);
      --dk-golge: 0 8px 28px rgba(0, 0, 0, 0.35);
    }
    :host-context(body:not(.tema-karanlik)) {
      --dk-cam: var(--dk-cam-acik);
      --dk-metin: var(--py-metin, #1a2030);
      --dk-kenar: rgba(0, 0, 0, 0.08);
    }
    /* Kök HUD: tüm viewport'u kaplar, merkez şeffaf, sahne orbit edilebilir */
    .dk-hud { position: fixed; inset: 0; pointer-events: none; }
    .dk-hud.dk-tikla-modu { cursor: crosshair; }

    .dk-cam-yuzey {
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(14px) saturate(140%);
      backdrop-filter: blur(14px) saturate(140%);
      border: 1px solid var(--dk-kenar);
      box-shadow: var(--dk-golge);
      color: var(--dk-metin);
    }

    /* Not rozeti */
    .dk-not {
      position: absolute; top: 14px; left: 14px;
      display: flex; align-items: center; gap: 12px;
      padding: 10px 14px 10px 10px; border-radius: 16px;
      pointer-events: auto;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(14px) saturate(140%);
      backdrop-filter: blur(14px) saturate(140%);
      border: 1px solid var(--dk-kenar);
      box-shadow: var(--dk-golge);
      color: var(--dk-metin);
      animation: dk-in 0.4s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-not-harf {
      width: 46px; height: 46px; border-radius: 13px;
      display: grid; place-items: center;
      font-size: 26px; font-weight: 800; color: #fff;
      background: linear-gradient(135deg, var(--dk-not-renk), color-mix(in srgb, var(--dk-not-renk) 60%, #000));
      box-shadow: 0 4px 14px color-mix(in srgb, var(--dk-not-renk) 50%, transparent);
    }
    .dk-not-bilgi { display: flex; flex-direction: column; gap: 3px; }
    .dk-not-sayilar { font-size: 12.5px; font-weight: 600; display: flex; gap: 6px; align-items: center; }
    .dk-uyari { color: #ffb02e; }
    .dk-kritik { color: #ff5b5b; }
    .dk-ayrac { opacity: 0.4; }
    .dk-not-ozet { font-size: 11px; opacity: 0.78; max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    /* Toolbar — üst-orta */
    .dk-toolbar {
      position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
      display: flex; align-items: center; gap: 6px;
      padding: 6px; border-radius: 14px;
      max-width: calc(100vw - 28px); flex-wrap: wrap; justify-content: center;
      pointer-events: auto;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(14px) saturate(140%);
      backdrop-filter: blur(14px) saturate(140%);
      border: 1px solid var(--dk-kenar);
      box-shadow: var(--dk-golge);
      color: var(--dk-metin);
      animation: dk-in 0.4s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-btn {
      border: 1px solid var(--dk-kenar); background: transparent;
      color: var(--dk-metin); font-size: 12.5px; font-weight: 600;
      padding: 7px 12px; border-radius: 10px; cursor: pointer;
      display: flex; align-items: center; gap: 6px;
      transition: transform 0.15s, background 0.15s, opacity 0.15s;
    }
    .dk-btn:hover:not(:disabled) { background: rgba(255,255,255,0.1); transform: translateY(-1px); }
    .dk-btn:disabled { opacity: 0.4; cursor: default; }
    .dk-btn-ana {
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      border-color: transparent; color: #fff;
    }
    .dk-ayrac-dik { width: 1px; align-self: stretch; background: var(--dk-kenar); margin: 2px 2px; }

    .dk-yolluk-secici, .dk-etiket-modu, .dk-yon-modu { display: flex; gap: 3px; }
    .dk-yon-modu .dk-mod { width: auto; padding: 0 9px; font-size: 11.5px; font-weight: 600; }
    .dk-yolluk-tip, .dk-mod {
      width: 32px; height: 32px; border-radius: 9px; cursor: pointer;
      border: 1px solid var(--dk-kenar); background: transparent;
      color: var(--dk-metin); font-size: 15px; display: grid; place-items: center;
      transition: transform 0.15s, background 0.15s, box-shadow 0.15s;
    }
    .dk-yolluk-tip:hover, .dk-mod:hover { background: rgba(255,255,255,0.1); transform: translateY(-1px); }
    .dk-yolluk-tip.aktif {
      background: color-mix(in srgb, var(--dk-y-renk) 24%, transparent);
      border-color: var(--dk-y-renk);
      box-shadow: 0 0 0 1px var(--dk-y-renk), 0 0 12px color-mix(in srgb, var(--dk-y-renk) 50%, transparent);
    }
    .dk-mod.aktif { background: rgba(59,130,246,0.25); border-color: #3b82f6; }

    /* İpucu — toolbar'ın altında, üst-orta */
    .dk-ipucu {
      position: absolute; top: 70px; left: 50%; transform: translateX(-50%);
      display: flex; align-items: center; gap: 8px;
      padding: 8px 14px; border-radius: 11px; font-size: 12.5px; font-weight: 600;
      pointer-events: none;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(14px);
      backdrop-filter: blur(14px);
      border: 1px solid var(--dk-kenar); box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-in 0.25s ease;
    }
    .dk-ipucu-renk { width: 12px; height: 12px; border-radius: 4px; }
    .dk-ipucu kbd { padding: 1px 6px; border-radius: 5px; background: rgba(255,255,255,0.15); font-size: 11px; }
    .dk-ipucu-btn {
      border: none; cursor: pointer; padding: 4px 12px; border-radius: 7px;
      font-size: 12px; font-weight: 600; color: #fff; pointer-events: auto;
      background: rgba(255,255,255,0.14); transition: background .15s, transform .1s;
    }
    .dk-ipucu-btn:hover { background: rgba(255,255,255,0.24); }
    .dk-ipucu-btn:active { transform: scale(0.95); }
    .dk-ipucu-btn.dk-onayla { background: linear-gradient(135deg, #16a34a, #22c55e); }
    .dk-ipucu-btn.dk-onayla:hover { background: linear-gradient(135deg, #15803d, #16a34a); }

    /* CSS2D sahne etiketleri (global — CSS2DRenderer DOM'una enjekte edilir,
       :host kapsamı dışında kaldıkları için ::ng-deep gibi davranır) */
    :host ::ng-deep .dk-sahne-etiket {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 9px; border-radius: 9px; white-space: nowrap;
      pointer-events: auto; cursor: pointer;
      font-family: 'Inter', system-ui, sans-serif;
      font-size: 11.5px; font-weight: 600;
      background: rgba(20, 24, 33, 0.78);
      -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
      border: 1px solid var(--dk-se-renk, #888);
      box-shadow: 0 4px 14px rgba(0,0,0,0.35), 0 0 0 1px color-mix(in srgb, var(--dk-se-renk, #888) 40%, transparent);
      color: #e8edf5;
      transform: translateY(-100%);
      transition: opacity 0.18s, transform 0.18s, box-shadow 0.18s;
    }
    :host ::ng-deep .dk-sahne-etiket:hover {
      transform: translateY(-100%) scale(1.06);
      box-shadow: 0 6px 20px color-mix(in srgb, var(--dk-se-renk, #888) 55%, transparent);
    }
    :host ::ng-deep .dk-sahne-etiket .nokta {
      width: 8px; height: 8px; border-radius: 50%;
      background: var(--dk-se-renk, #888); box-shadow: 0 0 8px var(--dk-se-renk, #888);
    }
    :host ::ng-deep .dk-sahne-etiket .deger { opacity: 0.78; font-weight: 700; }
    :host ::ng-deep .dk-sahne-etiket.cozuldu {
      opacity: 0.82; text-decoration: line-through; text-decoration-color: rgba(255,255,255,0.35);
    }
    :host ::ng-deep .dk-sahne-etiket.cozuldu .nokta {
      width: auto; height: auto; border-radius: 0; background: none; box-shadow: none;
      color: #22c55e; font-weight: 900; font-size: 12px; text-shadow: 0 0 6px #22c55e;
    }

    /* Detay kartı */
    .dk-detay {
      position: absolute; bottom: 64px; right: 14px; width: 250px;
      padding: 12px 14px; border-radius: 14px;
      pointer-events: auto;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(16px) saturate(140%);
      backdrop-filter: blur(16px) saturate(140%);
      border: 1px solid var(--dk-kenar); border-left: 3px solid var(--dk-d-renk);
      box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-up 0.3s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-detay-bas { display: flex; justify-content: space-between; align-items: center; }
    .dk-detay-tip { font-size: 13px; font-weight: 700; color: var(--dk-d-renk); }
    .dk-kapat-ana {
      position: fixed; top: 14px; right: 18px; z-index: 30; pointer-events: auto;
      width: 34px; height: 34px; border-radius: 50%; border: 1px solid var(--dk-kenar);
      background: var(--dk-cam); color: var(--dk-metin); cursor: pointer; font-size: 15px;
      -webkit-backdrop-filter: blur(14px); backdrop-filter: blur(14px); box-shadow: var(--dk-golge);
      transition: background .15s, transform .1s;
    }
    .dk-kapat-ana:hover { background: rgba(239,68,68,0.85); color: #fff; }
    .dk-kapat-ana:active { transform: scale(0.92); }
    .dk-detay-kapat { background: none; border: none; color: var(--dk-metin); opacity: 0.6; cursor: pointer; font-size: 13px; }
    .dk-detay-kapat:hover { opacity: 1; }
    .dk-detay-deger { font-size: 22px; font-weight: 800; margin: 4px 0; }
    .dk-detay-aciklama { font-size: 12px; opacity: 0.85; line-height: 1.45; margin: 4px 0 0; }
    .dk-detay-oneri { font-size: 11.5px; opacity: 0.9; margin: 8px 0 0; padding: 7px 9px; border-radius: 9px; background: rgba(59,130,246,0.12); }
    .dk-detay-cozum { font-size: 11.5px; font-weight: 600; color: #22c55e; margin: 8px 0 0; padding: 7px 9px; border-radius: 9px; background: rgba(34,197,94,0.14); border: 1px solid rgba(34,197,94,0.3); }

    /* Legend */
    .dk-legend {
      position: absolute; bottom: 14px; left: 50%; transform: translateX(-50%);
      display: flex; gap: 4px; padding: 6px;
      border-radius: 13px; pointer-events: auto;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(14px) saturate(140%);
      backdrop-filter: blur(14px) saturate(140%);
      border: 1px solid var(--dk-kenar); box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-up 0.4s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-legend-oge { display: flex; align-items: center; gap: 6px; padding: 5px 10px; border-radius: 9px; font-size: 11.5px; font-weight: 600; transition: background 0.15s; }
    .dk-legend-oge:hover { background: rgba(255,255,255,0.07); }
    .dk-legend-renk { width: 11px; height: 11px; border-radius: 4px; box-shadow: 0 0 7px currentColor; }
    .dk-legend-say { opacity: 0.6; font-weight: 700; min-width: 12px; text-align: right; }

    .dk-transform-modu { display: flex; gap: 3px; }

    /* Yolluk listesi paneli (sol) */
    .dk-yolluk-panel {
      position: absolute; top: 80px; left: 14px; width: 218px; max-height: 46vh;
      display: flex; flex-direction: column;
      border-radius: 14px; pointer-events: auto; overflow: hidden;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(14px) saturate(140%);
      backdrop-filter: blur(14px) saturate(140%);
      border: 1px solid var(--dk-kenar); box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-in 0.35s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-yp-bas { font-size: 12.5px; font-weight: 700; padding: 9px 12px; display: flex; align-items: center; gap: 6px; border-bottom: 1px solid var(--dk-kenar); }
    .dk-yp-say { margin-left: auto; opacity: 0.6; font-weight: 700; }
    .dk-yp-liste { overflow-y: auto; padding: 4px; display: flex; flex-direction: column; gap: 2px; }
    .dk-yp-satir {
      display: flex; align-items: center; gap: 7px; padding: 6px 8px; border-radius: 9px;
      cursor: pointer; font-size: 12px; font-weight: 600;
      border: 1px solid transparent; transition: background 0.15s, border-color 0.15s;
    }
    .dk-yp-satir:hover { background: rgba(255,255,255,0.08); }
    .dk-yp-satir.secili { background: color-mix(in srgb, var(--dk-y-renk) 18%, transparent); border-color: var(--dk-y-renk); }
    .dk-yp-simge { font-size: 14px; }
    .dk-yp-ad { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .dk-yp-hayalet { opacity: 0.55; }
    .dk-yp-durum { color: #22c55e; font-weight: 800; }
    .dk-yp-durum.gecersiz { color: #ef4444; }
    .dk-yp-sil { background: none; border: none; cursor: pointer; opacity: 0.5; font-size: 13px; padding: 0 2px; transition: opacity 0.15s, transform 0.15s; }
    .dk-yp-sil:hover { opacity: 1; transform: scale(1.15); }

    /* Özellik paneli (seçili yolluk) */
    .dk-ozellik {
      position: absolute; bottom: 64px; left: 14px; width: 250px;
      padding: 12px 14px; border-radius: 14px; pointer-events: auto;
      display: flex; flex-direction: column; gap: 9px;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(16px) saturate(140%);
      backdrop-filter: blur(16px) saturate(140%);
      border: 1px solid var(--dk-kenar); border-left: 3px solid var(--dk-o-renk);
      box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-up 0.3s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-ozellik-bas { display: flex; justify-content: space-between; align-items: center; }
    .dk-ozellik-tip { font-size: 13px; font-weight: 700; color: var(--dk-o-renk); }
    .dk-slider-satir { display: flex; flex-direction: column; gap: 4px; }
    .dk-slider-et { font-size: 11.5px; opacity: 0.85; }
    .dk-slider-et b { font-weight: 700; opacity: 1; }
    .dk-slider-satir input[type="range"] { width: 100%; accent-color: var(--dk-o-renk); cursor: pointer; }
    .dk-profil-satir { display: flex; flex-direction: column; gap: 5px; }
    .dk-profil-butonlar { display: flex; gap: 4px; flex-wrap: wrap; }
    .dk-profil-butonlar .dk-mod { flex: 1; min-width: 48px; font-size: 11px; }
    .dk-sensor-okuma { display: flex; flex-direction: column; gap: 4px; padding: 8px 10px; border-radius: 10px; background: rgba(225,29,143,0.1); border: 1px solid rgba(225,29,143,0.28); }
    .dk-sensor-satir { display: flex; justify-content: space-between; font-size: 11.5px; }
    .dk-sensor-satir b { font-weight: 700; }
    .dk-sensor-bos { font-size: 11px; opacity: 0.7; font-style: italic; }
    .dk-soguma-baslik { font-size: 10.5px; opacity: 0.8; margin-top: 6px; }
    .dk-soguma-svg { width: 100%; height: 40px; background: rgba(0,0,0,0.2); border-radius: 6px; margin-top: 2px; }
    /* Kusur raporu */
    .dk-rapor {
      position: absolute; top: 64px; left: 50%; transform: translateX(-50%);
      width: 320px; max-height: 72vh; overflow: auto; pointer-events: auto;
      padding: 12px 14px; border-radius: 14px; z-index: 21;
      background: var(--dk-cam); -webkit-backdrop-filter: blur(16px); backdrop-filter: blur(16px);
      border: 1px solid var(--dk-kenar); box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-up 0.3s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-rapor-bas { display: flex; justify-content: space-between; align-items: center; font-weight: 700; font-size: 13px; margin-bottom: 8px; }
    .dk-rapor-arac { display: flex; gap: 6px; align-items: center; }
    .dk-rapor-btn { background: rgba(255,255,255,0.08); border: 1px solid var(--dk-kenar); color: var(--dk-metin); border-radius: 8px; padding: 3px 8px; font-size: 11px; cursor: pointer; transition: background .15s; }
    .dk-rapor-btn:hover { background: rgba(255,255,255,0.18); }
    .dk-rapor-bolum { margin-bottom: 10px; }
    .dk-rapor-bolum-bas { font-size: 11.5px; font-weight: 700; opacity: 0.88; margin-bottom: 4px; border-bottom: 1px solid var(--dk-kenar); padding-bottom: 3px; }
    .dk-rapor-satir { font-size: 11.5px; line-height: 1.5; opacity: 0.92; }
    .dk-rapor-satir.iyi { color: #22c55e; }
    .dk-rapor-satir.kotu { color: #ff8c5a; }
    /* Dökülebilirlik skoru */
    .dk-cast { display: flex; align-items: center; gap: 12px; padding: 10px 12px; margin-bottom: 10px; border-radius: 12px; background: color-mix(in srgb, var(--dk-cast-renk) 14%, transparent); border: 1px solid color-mix(in srgb, var(--dk-cast-renk) 45%, transparent); }
    .dk-cast-skor { display: flex; align-items: baseline; }
    .dk-cast-puan { font-size: 32px; font-weight: 800; color: var(--dk-cast-renk); line-height: 1; }
    .dk-cast-yuz { font-size: 13px; opacity: 0.6; }
    .dk-cast-baslik { font-size: 11px; opacity: 0.75; text-transform: uppercase; letter-spacing: 0.5px; }
    .dk-cast-seviye { font-size: 15px; font-weight: 700; color: var(--dk-cast-renk); }
    .dk-cast-oneri { font-size: 11.5px; line-height: 1.45; padding: 5px 0; display: flex; gap: 6px; align-items: flex-start; }
    .dk-cast-rozet { font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 2px 6px; border-radius: 6px; background: rgba(148,163,184,0.25); white-space: nowrap; flex-shrink: 0; }
    .dk-cast-oneri.yuksek .dk-cast-rozet { background: rgba(239,68,68,0.3); color: #ff6b6b; }
    .dk-cast-oneri.orta .dk-cast-rozet { background: rgba(234,179,8,0.28); color: #eab308; }
    /* Görsel ayarlar paneli */
    .dk-gorsel-ayar {
      position: absolute; top: 64px; left: 14px; width: 250px; z-index: 21; pointer-events: auto;
      max-height: 78vh; overflow-y: auto;
      padding: 12px 14px; border-radius: 14px; display: flex; flex-direction: column; gap: 10px;
      background: var(--dk-cam); -webkit-backdrop-filter: blur(16px); backdrop-filter: blur(16px);
      border: 1px solid var(--dk-kenar); box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-up 0.3s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-ga-bolum-bas { font-size: 11.5px; font-weight: 700; opacity: 0.9; padding: 6px 0 2px; border-bottom: 1px solid var(--dk-kenar); margin-top: 4px; }
    .dk-gorsel-ayar small { opacity: 0.55; font-weight: 400; }
    .dk-ga-bas { display: flex; justify-content: space-between; align-items: center; font-weight: 700; font-size: 13px; }
    .dk-ga-not { font-size: 10.5px; opacity: 0.7; line-height: 1.4; }
    .dk-ga-bolme { height: 1px; background: var(--dk-kenar); margin: 2px 0; }
    .dk-gorsel-ayar input[type="range"] { width: 100%; accent-color: #38bdf8; cursor: pointer; }
    /* Proses penceresi (DOE) */
    .dk-varyant {
      position: absolute; top: 64px; left: 50%; transform: translateX(-50%); z-index: 22; pointer-events: auto;
      width: 280px; padding: 12px 14px; border-radius: 14px;
      background: var(--dk-cam); -webkit-backdrop-filter: blur(16px); backdrop-filter: blur(16px);
      border: 1px solid var(--dk-kenar); box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-up 0.3s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-varyant-not { font-size: 10.5px; opacity: 0.7; margin: 4px 0 8px; }
    .dk-varyant-tablo { width: 100%; border-collapse: collapse; font-size: 12px; }
    .dk-varyant-tablo th { text-align: left; font-size: 10.5px; opacity: 0.7; padding: 3px 6px; border-bottom: 1px solid var(--dk-kenar); }
    .dk-varyant-tablo td { padding: 5px 6px; border-bottom: 1px solid rgba(255,255,255,0.05); }
    .dk-varyant-tablo tr.iyi td { background: rgba(34,197,94,0.12); color: #22c55e; font-weight: 700; }
    .dk-v-en { font-size: 9.5px; margin-left: 4px; }
    .dk-ozellik-yon { font-size: 11px; opacity: 0.8; display: flex; flex-direction: column; gap: 2px; }
    .dk-ozellik-yon code { font-size: 11px; opacity: 0.9; }
    .dk-ozellik-ipucu { font-size: 10.5px; opacity: 0.6; font-style: italic; }
    .dk-ozellik-dogrulama { font-size: 11.5px; font-weight: 600; padding: 6px 9px; border-radius: 9px; background: rgba(34,197,94,0.14); color: #22c55e; }
    .dk-ozellik-dogrulama.gecersiz { background: rgba(239,68,68,0.14); color: #ff6b6b; }
    .dk-ozellik-butonlar { display: flex; gap: 6px; }
    .dk-ozellik-butonlar .dk-btn { flex: 1; justify-content: center; }
    .dk-btn-sil { border-color: rgba(239,68,68,0.5); color: #ff6b6b; }
    .dk-btn-sil:hover:not(:disabled) { background: rgba(239,68,68,0.15); }

    .dk-spin {
      width: 13px; height: 13px; border-radius: 50%;
      border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
      animation: dk-spin 0.7s linear infinite;
    }
    @keyframes dk-spin { to { transform: rotate(360deg); } }
    @keyframes dk-in { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes dk-up { from { opacity: 0; transform: translateX(-50%) translateY(10px); } to { opacity: 1; } }

    /* Döküm / yerçekimi yön seçici */
    .dk-yon-secici { display: flex; align-items: center; gap: 5px; }
    .dk-yon-ok { font-size: 14px; opacity: 0.85; }
    .dk-yon-butonlar { display: flex; gap: 2px; }
    .dk-yon-btn { width: 26px; height: 26px; font-size: 10.5px; font-weight: 700; border-radius: 7px; }
    .dk-yon-btn.aktif { background: rgba(34,197,94,0.25); border-color: #22c55e; }
    .dk-yon-etiket { font-size: 9.5px; font-weight: 700; opacity: 0.7; white-space: nowrap; max-width: 92px; overflow: hidden; text-overflow: ellipsis; }

    /* Alaşım & kum seçici */
    .dk-malzeme-secici { display: flex; gap: 6px; align-items: center; }
    .dk-ms-grup { display: flex; flex-direction: column; gap: 2px; }
    .dk-ms-et { font-size: 9.5px; font-weight: 700; opacity: 0.6; text-transform: uppercase; letter-spacing: 0.04em; padding-left: 2px; }
    .dk-ms-select {
      border: 1px solid var(--dk-kenar); border-radius: 8px; cursor: pointer;
      background: rgba(255,255,255,0.06); color: var(--dk-metin);
      font-size: 11.5px; font-weight: 600; padding: 5px 7px; max-width: 132px;
      transition: background 0.15s, border-color 0.15s;
    }
    .dk-ms-select:hover { background: rgba(255,255,255,0.12); border-color: rgba(59,130,246,0.5); }
    .dk-ms-select option { color: #1a2030; }

    /* Döküm Hesabı paneli */
    .dk-methoding {
      position: absolute; top: 80px; right: 10px; width: 240px; max-height: 64vh;
      display: flex; flex-direction: column; gap: 9px; overflow-y: auto;
      padding: 12px 14px; border-radius: 14px; pointer-events: auto;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(16px) saturate(140%);
      backdrop-filter: blur(16px) saturate(140%);
      border: 1px solid var(--dk-kenar); border-left: 3px solid #3b82f6;
      box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-in 0.35s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-mh-bas { display: flex; flex-direction: column; gap: 2px; }
    .dk-mh-baslik { font-size: 13px; font-weight: 800; }
    .dk-mh-malzeme { font-size: 11px; opacity: 0.75; }
    .dk-mh-izgara { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
    .dk-mh-oge { display: flex; flex-direction: column; gap: 1px; }
    .dk-mh-et { font-size: 10px; opacity: 0.62; font-weight: 600; }
    .dk-mh-deg { font-size: 13px; font-weight: 700; }
    .dk-mh-alt { font-size: 9.5px; opacity: 0.55; }
    .dk-mh-bolum { display: flex; flex-direction: column; gap: 3px; border-top: 1px solid var(--dk-kenar); padding-top: 8px; }
    .dk-mh-bolum-bas { font-size: 10px; font-weight: 700; opacity: 0.6; text-transform: uppercase; letter-spacing: 0.04em; }
    .dk-mh-satir { display: flex; align-items: flex-start; gap: 6px; font-size: 11px; font-weight: 500; line-height: 1.35; }
    .dk-mh-isaret { font-weight: 800; flex: 0 0 auto; }
    .dk-mh-mesaj { flex: 1; }
    .dk-mh-satir.gecersiz .dk-mh-mesaj { color: #ff8585; }
    .dk-mh-uyari { color: #fbbf24; }
    .dk-mh-nokta { width: 7px; height: 7px; border-radius: 50%; background: #f97316; box-shadow: 0 0 6px #f97316; flex: 0 0 auto; margin-top: 4px; }

    /* Dolum simülasyonu zaman çizelgesi (alt-orta, legend üstünde) */
    .dk-dolum {
      position: absolute; bottom: 62px; left: 50%; transform: translateX(-50%);
      display: flex; flex-direction: column; gap: 7px; min-width: 420px; max-width: calc(100vw - 28px);
      padding: 10px 14px; border-radius: 14px; pointer-events: auto;
      background: var(--dk-cam);
      -webkit-backdrop-filter: blur(16px) saturate(140%);
      backdrop-filter: blur(16px) saturate(140%);
      border: 1px solid var(--dk-kenar); border-left: 3px solid #38bdf8;
      box-shadow: var(--dk-golge); color: var(--dk-metin);
      animation: dk-up 0.3s cubic-bezier(0.2, 1, 0.3, 1);
    }
    .dk-dolum-ust { display: flex; align-items: center; gap: 10px; }
    .dk-dolum-oynat {
      width: 34px; height: 34px; border-radius: 50%; flex: 0 0 auto; cursor: pointer;
      border: 1px solid transparent; color: #fff; font-size: 14px;
      background: linear-gradient(135deg, #0ea5e9, #2563eb);
      display: grid; place-items: center; transition: transform 0.12s, box-shadow 0.15s;
      box-shadow: 0 3px 12px rgba(56,189,248,0.45);
    }
    .dk-dolum-oynat:hover { transform: translateY(-1px) scale(1.05); }
    .dk-dolum-oynat:active { transform: scale(0.94); }
    .dk-dolum-slider { flex: 1; accent-color: #38bdf8; cursor: pointer; height: 4px; }
    .dk-dolum-sure { font-size: 11.5px; font-weight: 700; opacity: 0.85; white-space: nowrap; min-width: 92px; text-align: right; font-variant-numeric: tabular-nums; }
    .dk-dolum-kapat { background: none; border: none; color: var(--dk-metin); opacity: 0.55; cursor: pointer; font-size: 14px; flex: 0 0 auto; transition: opacity 0.15s, transform 0.1s; }
    .dk-dolum-kapat:hover { opacity: 1; transform: scale(1.12); }
    .dk-dolum-ozet { display: flex; flex-wrap: wrap; gap: 6px; }
    .dk-dolum-rozet {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 11px; font-weight: 700; padding: 4px 9px; border-radius: 8px;
      border: 1px solid transparent;
    }
    .dk-r-hava  { background: rgba(56,189,248,0.16); color: #38bdf8; border-color: rgba(56,189,248,0.4); }
    .dk-r-soguk { background: rgba(96,165,250,0.16); color: #93c5fd; border-color: rgba(96,165,250,0.4); }
    .dk-r-eksik { background: rgba(239,68,68,0.16); color: #ff6b6b; border-color: rgba(239,68,68,0.4); }
    .dk-r-temiz { background: rgba(34,197,94,0.16); color: #22c55e; border-color: rgba(34,197,94,0.4); }
    .dk-r-cekme   { background: rgba(239,68,68,0.18); color: #ff5b5b; border-color: rgba(239,68,68,0.5); }
    .dk-r-sicak   { background: rgba(249,115,22,0.16); color: #fb923c; border-color: rgba(249,115,22,0.45); }
    .dk-r-yirtilma{ background: rgba(168,85,247,0.16); color: #c084fc; border-color: rgba(168,85,247,0.45); }

    /* Katılaşma zaman çizelgesi — dolum panelinin üstünde, sıcak kırmızı vurgu */
    .dk-katilasma {
      bottom: 138px;
      border-left-color: #ef4444;
    }
    .dk-katilasma .dk-kat-oynat { background: linear-gradient(135deg, #f97316, #ef4444); box-shadow: 0 3px 12px rgba(239,68,68,0.45); }
    .dk-katilasma .dk-kat-slider { accent-color: #ef4444; }

    /* Sıcaklık renk skalası (heat-map legend) */
    .dk-legend-isi {
      position: absolute; right: 16px; top: 50%; transform: translateY(-50%); z-index: 20;
      pointer-events: none; display: flex; flex-direction: column; align-items: center; gap: 6px;
      padding: 10px 10px; border-radius: 12px; background: var(--dk-cam);
      -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px);
      border: 1px solid var(--dk-kenar); box-shadow: var(--dk-golge); color: var(--dk-metin);
    }
    .dk-li-baslik { font-size: 11px; font-weight: 700; }
    .dk-li-cubuk {
      position: relative; width: 18px; height: 180px; border-radius: 6px;
      border: 1px solid rgba(255,255,255,0.18);
      background: linear-gradient(to top, #101218 0%, #3a1028 18%, #90120e 30%, #e83300 48%, #ff7a00 66%, #ffd033 84%, #fff6e0 100%);
    }
    .dk-li-deg { position: absolute; right: 26px; white-space: nowrap; font-size: 10.5px; font-weight: 600; opacity: 0.9; }
    .dk-li-ust { top: -3px; }
    .dk-li-orta { top: 50%; transform: translateY(-50%); }
    .dk-li-alt { bottom: -3px; }
    .dk-li-not { font-size: 9.5px; opacity: 0.6; }
    .dk-kat-bilgi { font-size: 10.5px; font-weight: 600; opacity: 0.7; }
    .dk-kat-uyarilar { display: flex; flex-direction: column; gap: 3px; border-top: 1px solid var(--dk-kenar); padding-top: 6px; }
    .dk-kat-uyari-satir { display: flex; align-items: flex-start; gap: 6px; font-size: 11px; font-weight: 500; line-height: 1.35; color: #fbbf24; }

    @media (max-width: 640px) {
      .dk-dolum { min-width: 0; width: calc(100vw - 28px); }
      .dk-not-ozet { display: none; }
      .dk-legend-ad { display: none; }
      .dk-toolbar { flex-wrap: wrap; max-width: calc(100vw - 28px); }
      .dk-methoding { width: calc(100vw - 28px); right: 14px; }
    }
  `],
})
export class FikoDokumKontrolComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  @Input() anaGosterim: any = null;
  @Input() pencere: any = null;
  @Input() anaNesne: any = null;
  kapat = output<void>();

  // ───────── Sabit listeler (template trackBy için) ─────────
  readonly gostergeTipleri: GostergeTipi[] = ['ince_cidar', 'sicak_nokta', 'ani_kesit', 'hapsolan_hava'];
  readonly yollukTipleri: YollukTipi[] = ['dolum_girisi', 'besleyici', 'hava_deligi', 'filtre', 'sogutucu', 'maca', 'tasma', 'sensor'];
  readonly etiketModlari: { deger: EtiketModu; baslik: string; simge: string }[] = [
    { deger: 'hepsi', baslik: 'Tüm etiketler', simge: '👁️' },
    { deger: 'hover', baslik: 'Üstüne gelince', simge: '🖱️' },
    { deger: 'secim', baslik: 'Seçince', simge: '🎯' },
    { deger: 'gizli', baslik: 'Gizli', simge: '🚫' },
  ];
  private readonly yollukSimgeler: Record<YollukTipi, string> = {
    dolum_girisi: '⬇️', besleyici: '⭕', hava_deligi: '🔺', filtre: '▦',
    sogutucu: '❄️', maca: '⬛', tasma: '🫗', sensor: '📍',
  };
  readonly akisProfilleri: { deger: AkisProfili; etiket: string; baslik: string }[] = [
    { deger: 'sabit', etiket: 'Sabit', baslik: 'Sabit debi' },
    { deger: 'rampa', etiket: 'Rampa', baslik: 'Yavaş başla, debiyi artır (türbülans azaltır)' },
    { deger: 'sinus', etiket: 'Sinüs', baslik: 'Dalgalı debi' },
    { deger: 'puls', etiket: 'Puls', baslik: 'Aç/kıs darbeli debi' },
  ];
  readonly besleyiciTipleri: { deger: BesleyiciTipi; etiket: string; baslik: string }[] = [
    { deger: 'acik', etiket: 'Açık', baslik: 'Atmosfere açık besleyici (üstü hava)' },
    { deger: 'kor', etiket: 'Kör', baslik: 'Kör/blind besleyici (her tarafı kum)' },
    { deger: 'ekzotermik', etiket: 'Ekzo', baslik: 'Ekzotermik gömlek — yanar, ısı verir → çok uzun besler' },
    { deger: 'yalitimli', etiket: 'Yalıtım', baslik: 'Yalıtım gömleği — ısı kaybı azalır → geç katılaşır' },
  ];
  readonly sogutucuMalzemeleri: { deger: SogutucuMalzeme; etiket: string; baslik: string }[] = [
    { deger: 'celik', etiket: 'Çelik', baslik: 'Çelik chill — 6× ısı çekişi' },
    { deger: 'bakir', etiket: 'Bakır', baslik: 'Bakır chill — 12× ısı çekişi (en hızlı)' },
    { deger: 'grafit', etiket: 'Grafit', baslik: 'Grafit chill — 8× ısı çekişi' },
  ];
  readonly girisYonModlari: { deger: GirisYonModu; etiket: string; baslik: string }[] = [
    { deger: 'otomatik', etiket: 'Otomatik', baslik: 'Yön yüzey normalinden otomatik belirlenir' },
    { deger: 'iki_tik', etiket: 'İki-tık', baslik: 'Önce konum, sonra yön için ikinci nokta' },
    { deger: 'surukle', etiket: 'Sürükle', baslik: 'Oku sürükleyerek yönü ayarla' },
  ];
  readonly transformModlari: { deger: TransformMod; simge: string; baslik: string }[] = [
    { deger: 'sec', simge: '🖱️', baslik: 'Seç' },
    { deger: 'tasi', simge: '✥', baslik: 'Taşı' },
    { deger: 'dondur', simge: '🔄', baslik: 'Döndür' },
  ];
  /** Döküm/akış (yerçekimi) yönü — yukari ekseni; metal yukari'nın tersine akar */
  readonly yonSecenekleri: IYonSecenek[] = [
    { deger: '+Y', etiket: 'Üst (+Y)',  baslik: 'Üst yukarı — metal aşağı (−Y) akar',  yukari: [0, 1, 0] },
    { deger: '-Y', etiket: 'Alt (−Y)',  baslik: 'Alt yukarı — metal yukarı (+Y) akar',  yukari: [0, -1, 0] },
    { deger: '+X', etiket: 'Sağ (+X)',  baslik: 'Sağ yukarı — metal sola (−X) akar',    yukari: [1, 0, 0] },
    { deger: '-X', etiket: 'Sol (−X)',  baslik: 'Sol yukarı — metal sağa (+X) akar',    yukari: [-1, 0, 0] },
    { deger: '+Z', etiket: 'Ön (+Z)',   baslik: 'Ön yukarı — metal arkaya (−Z) akar',   yukari: [0, 0, 1] },
    { deger: '-Z', etiket: 'Arka (−Z)', baslik: 'Arka yukarı — metal öne (+Z) akar',    yukari: [0, 0, -1] },
  ];

  // ───────── Durum sinyalleri ─────────
  sonuc = signal<IKontrolSonuc | null>(null);
  calisiyor = signal(false);
  ilerleme = signal(0);
  /** Çalışan simülasyon aşaması (buton ilerleme etiketi için) */
  simAsama = signal<'dolum' | 'katilasma' | null>(null);
  aktifYollukTip = signal<YollukTipi | null>(null);
  tiklamaModu = signal<YollukTipi | null>(null);
  etiketModu = signal<EtiketModu>('hepsi');
  girisYonModu = signal<GirisYonModu>('otomatik');
  seciliBolge = signal<IDokumBolge | null>(null);
  hoverBolgeId = signal<string | null>(null);
  yolluklar = signal<IYollukEleman[]>([]);
  seciliYolluk = signal<IYollukEleman | null>(null);
  transformMod = signal<TransformMod>('sec');
  /** Seçili döküm/yerçekimi yönü (yukari ekseni etiketi) */
  yukariYon = signal<string>('+Y');
  yon3dSecModu = signal(false);
  /** Kusur raporu paneli açık mı */
  raporAcik = signal(false);
  /** Proses penceresi (DOE) varyant sonuçları */
  varyantSonuc = signal<{ tempC: number; coldShut: number; misrun: number; iyi: boolean }[] | null>(null);
  varyantCalisiyor = signal(false);

  // ───────── Görsel ayarlar (voxel boyut/renk/çözünürlük) ─────────
  gorselAyarAcik = signal(false);
  voxelBoyut = signal(0.72);     // küre yarıçap faktörü (0.3 küçük → 1.0 örtüşen)
  voxelParlaklik = signal(1.0);  // renk parlaklığı
  glowAcik = signal(true);       // akkor glow halesi
  pruzsuzAcik = signal(false);   // pürüzsüz yüzey (surface nets) — opt-in
  pruzsuzKalinlik = signal(1);   // yüzey kalınlığı (dilation 1-4)
  voxelCozunurluk = signal(0);   // 0 = otomatik; >0 = en uzun eksende hücre
  cozumKalite = signal<'onizleme' | 'final'>('onizleme'); // final = SPH WebGPU dolum
  kesitEksen = signal<'kapali' | 'x' | 'y' | 'z'>('kapali');
  kesitKonum = signal(0.5);
  // Analiz hassasiyeti (göstergeler) — değişince yeniden Kontrol Et
  ayarMinCidar = signal(0);       // 0 = otomatik (alaşıma göre); >0 mm
  ayarSicakModul = signal(0.8);   // sıcak nokta modülü cm (büyük = daha az tespit)
  ayarKesitOran = signal(2.5);    // ani kesit oran eşiği
  ayarMinBolge = signal(8);       // min bölge vertex (büyük = gürültü azalır)
  // Yolluk önerisi
  ayarMaxBesleyici = signal(6);   // max otomatik besleyici sayısı
  ayarBesleyiciCap = signal(60);  // besleyici max çap (mm)
  // Döküm prosesi
  ayarDokumSicaklik = signal(0);  // 0 = otomatik (alaşım); >0 °C
  ayarKalipSicaklik = signal(25); // kalıp ön ısıtma °C
  readonly kesitEksenleri: { deger: 'kapali' | 'x' | 'y' | 'z'; etiket: string }[] = [
    { deger: 'kapali', etiket: 'Kapalı' }, { deger: 'x', etiket: 'X' }, { deger: 'y', etiket: 'Y' }, { deger: 'z', etiket: 'Z' },
  ];
  /** Aktif görseli ayar değişince yeniden kurmak için son üretim verisi */
  private dolumGorselVeri: { kafes: any; sonuc: any } | null = null;
  private katilasmaGorselVeri: { kafes: any; sonuc: any; kareler?: any; min?: number; max?: number } | null = null;
  /** Sensör id → soğuma eğrisi T(t) serisi */
  private sogumaEgriMap = new Map<string, number[]>();

  // ───────── Gerçekçi render (dökümhane ortamı) ─────────
  private envMapTex: THREE.Texture | null = null;
  private dokumhaneAktif = false;
  private oncekiArkaplan: THREE.Color | THREE.Texture | null = null;
  private oncekiClipping = false;
  private zeminMesh: THREE.Mesh | null = null;

  // ───────── Methoding (döküm hesabı) durumu ─────────
  alasimId = signal<string>('Al_A356');
  kumId = signal<string>('kum_silika');
  methodingSonuc = signal<IMethodingSonuc | null>(null);
  alasimListesi: IMalzemeSecenek[] = [];
  kumListesi: IMalzemeSecenek[] = [];

  // ───────── Dolum simülasyonu durumu ─────────
  dolumAktif = signal(false);
  dolumOynuyor = signal(false);
  dolumZaman = signal(0);
  dolumSure = signal(0);
  dolumSonuc = signal<IDolumSonuc | null>(null);
  private dolumGorsel: FikoDolumGorsel | null = null;
  private dolumRaf: number | null = null;

  // ───────── Katılaşma simülasyonu durumu (Faz 2) ─────────
  katilasmaAktif = signal(false);
  katilasmaOynuyor = signal(false);
  katilasmaZaman = signal(0);
  katilasmaSure = signal(0);
  katilasmaSonuc = signal<IKatilasmaSonuc | null>(null);
  /** Termal heat-map skalası (°C) — legend için */
  katilasmaSicaklik = signal<{ min: number; max: number } | null>(null);
  private katilasmaGorsel: FikoDolumGorsel | null = null;
  private katilasmaRaf: number | null = null;
  /** Sahnede defekt işaretleri (çekme/sıcak nokta/yırtılma) */
  private katilasmaIsaretleri: THREE.Mesh[] = [];

  katilasmaBeslenenSayi = computed(() => this.katilasmaSonuc()?.sicakNoktalar.filter(s => s.beslendi).length ?? 0);
  katilasmaBeslenmeyenSayi = computed(() => this.katilasmaSonuc()?.sicakNoktalar.filter(s => !s.beslendi).length ?? 0);

  notRenk = computed(() => {
    const n = this.sonuc()?.not.not;
    return ({ A: '#22c55e', B: '#84cc16', C: '#eab308', D: '#f97316', F: '#ef4444' } as Record<string, string>)[n || 'C'] || '#eab308';
  });

  // ───────── 3D yapılar ─────────
  private motor: IDokumKontrolMotor | null = null;
  private duzenleyici: FikoYollukDuzenleyici | null = null;   // 3D yolluk düzenleyici motoru
  private isiHaritasiGrup: THREE.Group | null = null;
  private orijinalRenkler = new Map<THREE.Mesh, THREE.BufferAttribute | undefined>();
  private boyaliMeshler = new Set<THREE.Mesh>();
  /** Sahneye gömülü CSS2D etiketleri (+ işaret küresi + lider çizgi) */
  private sahneEtiketObjeleri: ISahneEtiket[] = [];
  /** Tip başına gösterilecek azami etiket + toplam tavan */
  private static readonly ETIKET_TIP_LIMIT = 3;
  private static readonly ETIKET_TOPLAM_LIMIT = 12;

  private tiklamaDinleyici: ((e: PointerEvent) => void) | null = null;
  private klavyeDinleyici: ((e: KeyboardEvent) => void) | null = null;

  private readonly raycaster = new THREE.Raycaster();
  private readonly _v = new THREE.Vector3();
  private readonly _fare = new THREE.Vector2();

  // ───────── Dolum girişi yön belirleme durumu ─────────
  /** İki-tık modu: 1. tıkta saklanan konum + yüzey normali */
  private bekleyenGiris: { pos: THREE.Vector3; normal: THREE.Vector3 } | null = null;
  /** İki-tık önizleme yönü (Onayla butonu için) */
  private bekleyenYon: THREE.Vector3 | null = null;
  /** İki-tık önizleme oku (geçici) */
  private onizlemeOk: THREE.ArrowHelper | null = null;
  /** Sürükle modu: o anda yönü düzenlenen elemanın id'si */
  private duzenlenenGirisId: string | null = null;
  /** Giriş yön düzenlemesi aktif mi (Onayla/İptal butonlarını göstermek için) */
  girisDuzenlemeAktif = signal(false);
  /** Sürükle sırasında pointer basılı mı */
  private surukluyor = false;
  private tiklamaMoveDinleyici: ((e: PointerEvent) => void) | null = null;
  private tiklamaUpDinleyici: ((e: PointerEvent) => void) | null = null;

  constructor() { super(); }

  ngOnInit(): void {
    const kayitli = this.guvenliLocalStorage('get', ETIKET_MODU_ANAHTAR) as EtiketModu | null;
    if (kayitli && this.etiketModlari.some(m => m.deger === kayitli)) {
      this.etiketModu.set(kayitli);
    }
    const yonKayitli = this.guvenliLocalStorage('get', GIRIS_YON_MODU_ANAHTAR) as GirisYonModu | null;
    if (yonKayitli && this.girisYonModlari.some(m => m.deger === yonKayitli)) {
      this.girisYonModu.set(yonKayitli);
    }
    const yonKayit = this.guvenliLocalStorage('get', YUKARI_YON_ANAHTAR);
    if (yonKayit && this.yonSecenekleri.some(y => y.deger === yonKayit)) {
      this.yukariYon.set(yonKayit);
    }
    this.malzemeListeleriniKur();
    try {
      this.motor = new FikoDokumKontrol(this.anaGosterim) as unknown as IDokumKontrolMotor;
      // Persist edilmiş döküm yönünü motora uygula
      const sec = this.yonSecenekleri.find(y => y.deger === this.yukariYon());
      if (sec) { try { this.motor.yukariYonAyarla?.(new THREE.Vector3(...sec.yukari)); } catch {} }
    } catch {
      this.motor = null; // orkestratör henüz hazır değilse sessiz geç
    }
    this.duzenleyiciKur();
    this.tiklamaModunuKur();
  }

  /** 3D yolluk düzenleyici motorunu kur (gizmo + highlight + seçim) */
  private duzenleyiciKur(): void {
    const kok = this.sahneGrubunaErisim();
    const kamera = this.kameraAl();
    const canvas = this.anaGosterim?.canvas?.nativeElement as HTMLElement | undefined;
    if (!kok || !kamera || !canvas) return;
    try {
      this.duzenleyici = new FikoYollukDuzenleyici({
        kok,
        kamera,
        domElement: canvas,
        orbitKontrol: this.anaGosterim?.aktifKontrol,
        yuzeyMeshler: () => this.aktifMeshleriTopla(),
        onDegisti: (id: string, kismi: IYollukGuncelleme) => {
          // Gizmo objeyi zaten hareket ettirdi → senkronize ÇAĞIRMA, sadece motoru güncelle
          try { this.motor?.yollukGuncelle(id, kismi); } catch {}
          this.seciliYollukTazele(id);
        },
        onSecimDegisti: (id: string | null) => {
          const el = id ? (this.motor?.yolluklar().find(y => y.id === id) ?? null) : null;
          this.seciliYolluk.set(el);
          this.CD.detectChanges();
        },
      });
    } catch {
      this.duzenleyici = null; // motor henüz hazır değilse sessiz geç
    }
  }

  /** Alaşım & kum listelerini DB'den doldur, persist edilmiş seçimleri uygula */
  private malzemeListeleriniKur(): void {
    const db = getDokumMalzemeDB();
    // Alaşımlar: alüminyumlar üstte, ardından diğer tüm alaşımlar
    const aluminyum = db.kategoriAlasimlar(DokumAlasimKategori.ALUMINYUM);
    const aluminyumIdler = new Set(aluminyum.map(a => a.id));
    const digerler = db.tumAlasimlar().filter(a => !aluminyumIdler.has(a.id));
    this.alasimListesi = [...aluminyum, ...digerler].map(a => ({ id: a.id, ad: a.ad }));
    this.kumListesi = db.kategoriKaliplar(KalipMalzemeKategori.KUM).map(k => ({ id: k.id, ad: k.ad }));

    // Varsayılan kum = ilk kum tipi
    if (this.kumListesi.length > 0) this.kumId.set(this.kumListesi[0].id);

    // Persist edilmiş seçimleri uygula (geçerliyse)
    const kayitliAlasim = this.guvenliLocalStorage('get', ALASIM_ANAHTAR);
    if (kayitliAlasim && this.alasimListesi.some(a => a.id === kayitliAlasim)) {
      this.alasimId.set(kayitliAlasim);
    }
    const kayitliKum = this.guvenliLocalStorage('get', KUM_ANAHTAR);
    if (kayitliKum && this.kumListesi.some(k => k.id === kayitliKum)) {
      this.kumId.set(kayitliKum);
    }
  }

  /** Methoding (döküm hesabı) sonucunu motordan tazele */
  private methodingYenile(): void {
    this.methodingSonuc.set(this.motor?.methodingHesapla(this.alasimId(), this.kumId()) ?? null);
    this.CD.detectChanges();
  }

  /** Alaşım seçici değişti */
  alasimSec(id: string): void {
    this.alasimId.set(id);
    this.guvenliLocalStorage('set', ALASIM_ANAHTAR, id);
    this.methodingYenile();
  }

  /** Kum seçici değişti */
  kumSec(id: string): void {
    this.kumId.set(id);
    this.guvenliLocalStorage('set', KUM_ANAHTAR, id);
    this.methodingYenile();
  }

  /** Seçili yolluk panel sinyalini motordan tazele */
  private seciliYollukTazele(id: string): void {
    const guncel = this.motor?.yolluklar().find(y => y.id === id) ?? null;
    if (guncel) this.seciliYolluk.set({ ...guncel });
    this.CD.detectChanges();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.dolumTemizle();
    this.katilasmaTemizle();
    this.girisYonDurumTemizle();
    this.tiklamaModuTemizle();
    this.etiketleriTemizle();
    this.isiHaritasiTemizle();
    try { this.duzenleyici?.dispose(); } catch {}
    try { this.motor?.dispose?.(); } catch {}
    try { this.envMapTex?.dispose(); } catch {}
    this.envMapTex = null;
    this.duzenleyici = null;
    this.motor = null;
  }

  // ════════════════════════════════════════════════════════════
  // ANALİZ
  // ════════════════════════════════════════════════════════════

  async kontrolEt(): Promise<void> {
    if (this.calisiyor() || !this.motor) {
      if (!this.motor) this.nzMessage.warning('Döküm kontrol motoru hazır değil.');
      return;
    }
    const meshler = this.aktifMeshleriTopla();
    if (meshler.length === 0) {
      this.nzMessage.warning('Analiz için parça bulunamadı.');
      return;
    }

    this.calisiyor.set(true);
    this.ilerleme.set(0);
    this.temizle();
    this.CD.detectChanges();
    await new Promise(r => setTimeout(r, 30)); // UI'nın spinner'ı çizmesine izin ver

    try {
      const sonuc = await this.motor.kontrolEt({
        meshler,
        ayar: this.analizAyar(),
        onProgress: (p: number) => {
          this.ilerleme.set(Math.round(p));
          this.CD.detectChanges();
        },
      });
      this.sonuc.set(sonuc);
      this.isiHaritasiCiz(sonuc);
      this.sahneEtiketleriKur(sonuc.gosterge.bolgeler);
      this.yolluklariYenile();   // korunan yollukları 3D'ye + listeye geri yansıt
      this.methodingYenile();
      this.nzNotification.success(
        'Döküm Kontrol',
        `Not: ${sonuc.not.not} · ${sonuc.not.toplamUyari} uyarı, ${sonuc.not.kritikSayi} kritik`,
      );
    } catch (e: any) {
      this.nzMessage.error('Analiz sırasında hata oluştu: ' + (e?.message ?? e));
    } finally {
      this.calisiyor.set(false);
      this.anaGosterim?.ciz?.();
      this.CD.detectChanges();
    }
  }

  yollukOner(): void {
    if (!this.motor || !this.sonuc()) return;
    try {
      this.motor.yollukOner();
      this.yolluklariYenile();
      this.nzMessage.info('Yolluk önerileri eklendi. Kabul etmek için tıklayın.');
    } catch (e: any) {
      this.nzMessage.error('Yolluk önerisi başarısız: ' + (e?.message ?? e));
    }
  }

  /** Otomatik gating tasarımı: göstergelere göre yolluk öner + hepsini kabul et. */
  otomatikDuzelt(): void {
    if (!this.motor || !this.sonuc()) return;
    try {
      const oneriler = this.motor.yollukOner();
      for (const o of oneriler) this.motor.yollukKabul(o.id);
      this.yolluklariYenile();
      this.nzMessage.success(`${oneriler.length} yolluk otomatik yerleştirildi ve kabul edildi.`);
    } catch (e: any) {
      this.nzMessage.error('Otomatik düzeltme başarısız: ' + (e?.message ?? e));
    }
  }

  // ════════════════════════════════════════════════════════════
  // DOLUM SİMÜLASYONU — voxel kavite + akış animasyonu (Faz 1)
  // ════════════════════════════════════════════════════════════

  /** Dolum simülasyonunu başlat: kaviteyi voxelle, fill-front hesapla, animasyonu oynat */
  async dolumSimBaslat(): Promise<void> {
    this.dolumTemizle();
    this.katilasmaTemizle();
    if (!this.motor || !this.sonuc()) {
      this.nzMessage.warning('Önce "Kontrol Et" ile analiz yapın.');
      return;
    }
    this.calisiyor.set(true); this.simAsama.set('dolum'); this.ilerleme.set(0);
    this.CD.detectChanges();
    await new Promise(r => setTimeout(r, 20)); // spinner çizilsin
    let r: IDolumSimSonuc | null = null;
    try {
      r = await this.motor.dolumSimulasyonu(this.alasimId(), (p) => { this.ilerleme.set(p); this.CD.detectChanges(); });
    } catch (e: any) {
      this.calisiyor.set(false); this.simAsama.set(null); this.CD.detectChanges();
      this.nzMessage.error('Dolum simülasyonu başarısız: ' + (e?.message ?? e));
      return;
    }
    this.calisiyor.set(false); this.simAsama.set(null); this.CD.detectChanges();
    if (!r) {
      this.nzMessage.warning('Dolum simülasyonu için dolum girişi gerekli — bir dolum girişi yollugu ekleyin.');
      return;
    }
    const kok = this.sahneGrubunaErisim();
    if (!kok) { this.nzMessage.error('Sahne erişimi yok.'); return; }

    // Isı haritası/etiketler akış görselini karıştırmasın → gizle (parça orijinal renge dönsün)
    this.etiketleriTemizle();
    this.isiHaritasiTemizle();

    try {
      this.dokumhaneKur();
      this.dolumGorselVeri = { kafes: r.kafes, sonuc: r.sonuc };
      this.dolumGorsel = new FikoDolumGorsel({
        kok, kafes: r.kafes, sonuc: r.sonuc, renkModu: 'zaman', envMap: this.envMapKur(),
        boyutFaktor: this.voxelBoyut(), parlaklik: this.voxelParlaklik(), glow: this.glowAcik(), pruzsuz: this.pruzsuzAcik(), pruzsuzKalinlik: this.pruzsuzKalinlik(),
      });
    } catch (e: any) {
      this.nzMessage.error('Dolum görseli oluşturulamadı: ' + (e?.message ?? e));
      return;
    }

    this.dolumSonuc.set(r.sonuc);
    this.dolumSure.set(r.sonuc.toplamSure);
    this.dolumZaman.set(0);
    this.dolumAktif.set(true);
    this.dolumGorsel.zamaniAyarla(0);
    this.sensorleriTazele();
    this.anaGosterim?.ciz?.();
    this.CD.detectChanges();
    this.dolumOynat();
  }

  /** Environment map (PMREM RoomEnvironment) — metalik yansıma için, bir kez üret. */
  private envMapKur(): THREE.Texture | null {
    if (this.envMapTex) return this.envMapTex;
    const renderer = this.anaGosterim?.sunucu as THREE.WebGLRenderer | undefined;
    if (!renderer) return null;
    try {
      const pmrem = new THREE.PMREMGenerator(renderer);
      const env = new RoomEnvironment();
      this.envMapTex = pmrem.fromScene(env as any, 0.04).texture;
      pmrem.dispose();
      (env as any).dispose?.();
    } catch { this.envMapTex = null; }
    return this.envMapTex;
  }

  /** Seçili yukarı (yerçekiminin tersi) yön vektörü. */
  private aktifYukariVek(): THREE.Vector3 {
    const s = this.yonSecenekleri.find(y => y.deger === this.yukariYon());
    return s ? new THREE.Vector3(...s.yukari) : new THREE.Vector3(0, 1, 0);
  }

  /** Karanlık dökümhane sahnesi: koyu arka plan + parçanın altına yansıtıcı zemin. */
  private dokumhaneKur(): void {
    const sahne = this.anaGosterim?.sahneler?.[0] as THREE.Scene | undefined;
    if (!sahne || this.dokumhaneAktif) return;
    this.dokumhaneAktif = true;
    this.oncekiArkaplan = (sahne.background as any) ?? null;
    sahne.background = new THREE.Color(0x07080c);
    // Kesit (clipping) düzlemleri için renderer'da yerel clipping aç
    const r = this.anaGosterim?.sunucu as THREE.WebGLRenderer | undefined;
    if (r) { this.oncekiClipping = r.localClippingEnabled; r.localClippingEnabled = true; }

    const geo = this.sonuc()?.geo;
    if (geo) {
      const boyut = geo.bbox.getSize(new THREE.Vector3());
      const merkez = geo.bbox.getCenter(new THREE.Vector3());
      const up = this.aktifYukariVek().normalize();
      const yariUst = 0.5 * (Math.abs(boyut.x * up.x) + Math.abs(boyut.y * up.y) + Math.abs(boyut.z * up.z));
      const r = Math.max(boyut.x, boyut.y, boyut.z) * 3 + 60;
      const zemin = new THREE.Mesh(
        new THREE.CircleGeometry(r, 64),
        new THREE.MeshStandardMaterial({
          color: 0x0b0c12, metalness: 0.55, roughness: 0.4,
          envMap: this.envMapKur(), envMapIntensity: 0.5,
        }),
      );
      // Zemini parçanın yerçekimi tabanına koy, normalini yukarı çevir
      zemin.position.copy(merkez).addScaledVector(up, -(yariUst + r * 0.02));
      zemin.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), up);
      zemin.renderOrder = -1;
      this.zeminMesh = zemin;
      sahne.add(zemin);
    }
    this.anaGosterim?.ciz?.();
  }

  /** Dökümhane sahnesini geri al (orijinal arka plan + zemini kaldır). */
  private dokumhaneKaldir(): void {
    if (!this.dokumhaneAktif) return;
    this.dokumhaneAktif = false;
    const sahne = this.anaGosterim?.sahneler?.[0] as THREE.Scene | undefined;
    if (sahne) sahne.background = this.oncekiArkaplan as any;
    const r = this.anaGosterim?.sunucu as THREE.WebGLRenderer | undefined;
    if (r) r.localClippingEnabled = this.oncekiClipping;
    if (this.zeminMesh) {
      this.zeminMesh.parent?.remove(this.zeminMesh);
      try { this.zeminMesh.geometry.dispose(); (this.zeminMesh.material as THREE.Material).dispose(); } catch {}
      this.zeminMesh = null;
    }
    this.anaGosterim?.ciz?.();
  }

  /** Analiz hassasiyeti override'ı (göstergeler) — kontrolEt'e geçer. */
  private analizAyar(): Partial<IGostergeAyar> {
    const a: Partial<IGostergeAyar> = {
      sicakNoktaModulCm: this.ayarSicakModul(),
      kesitOraniEsik: this.ayarKesitOran(),
      minBolgeVertex: Math.round(this.ayarMinBolge()),
    };
    if (this.ayarMinCidar() > 0) a.minCidarMm = this.ayarMinCidar();
    return a;
  }

  /** Analiz ayarı değişti — açık sonuç varsa yeniden analiz et (debounce'suz, kullanıcı bırakınca). */
  analizAyarDegisti(): void {
    if (this.sonuc() && !this.calisiyor()) this.kontrolEt();
  }

  /** Yolluk öneri ayarlarını motora uygula. */
  yollukAyarUygula(): void {
    try { this.motor?.yollukAyarla?.({ maxBesleyici: Math.round(this.ayarMaxBesleyici()), besleyiciMaxCapMm: this.ayarBesleyiciCap() }); } catch {}
  }

  /** Döküm prosesi ayarlarını motora uygula (sonraki sim'de etkili). */
  prosesAyarUygula(): void {
    try { this.motor?.prosesAyarla?.({ dokumSicaklikC: this.ayarDokumSicaklik(), kalipSicaklikC: this.ayarKalipSicaklik() }); } catch {}
  }

  // ───────── Görsel ayarlar ─────────
  gorselAyarToggle(): void { this.gorselAyarAcik.update(v => !v); }

  voxelBoyutDegisti(v: number): void { this.voxelBoyut.set(Number(v)); this.gorselYenidenKur(); }
  voxelParlaklikDegisti(v: number): void { this.voxelParlaklik.set(Number(v)); this.gorselYenidenKur(); }
  glowAyarla(deger: boolean): void { if (this.glowAcik() === deger) return; this.glowAcik.set(deger); this.gorselYenidenKur(); }
  pruzsuzAyarla(deger: boolean): void { if (this.pruzsuzAcik() === deger) return; this.pruzsuzAcik.set(deger); this.gorselYenidenKur(); }
  pruzsuzKalinlikDegisti(v: number): void { this.pruzsuzKalinlik.set(Number(v)); if (this.pruzsuzAcik()) this.gorselYenidenKur(); }
  voxelCozunurlukDegisti(v: number): void {
    const n = Number(v);
    this.voxelCozunurluk.set(n);
    this.motor?.dolumCozunurlukAyarla(n);
  }

  cozumKaliteSec(k: 'onizleme' | 'final'): void {
    if (this.cozumKalite() === k) return;
    if (k === 'final' && !(navigator as any).gpu) {
      this.nzMessage.warning('WebGPU desteklenmiyor — Önizleme (CA) kullanılacak.');
      return;
    }
    this.cozumKalite.set(k);
    this.motor?.cozumKalitesiAyarla?.(k);
  }

  kesitEksenSec(e: 'kapali' | 'x' | 'y' | 'z'): void { this.kesitEksen.set(e); this.kesitUygula(); }
  kesitKonumDegisti(v: number): void { this.kesitKonum.set(Number(v)); this.kesitUygula(); }

  /** Aktif görsele kesit düzlemini uygula. */
  private kesitUygula(): void {
    const e = this.kesitEksen();
    const eksen = e === 'kapali' ? null : e;
    const oran = this.kesitKonum();
    try { this.dolumGorsel?.kesitAyarla(eksen, oran); } catch {}
    try { this.katilasmaGorsel?.kesitAyarla(eksen, oran); } catch {}
    this.anaGosterim?.ciz?.();
  }

  /** Aktif dolum/katılaşma görselini güncel ayarlarla (boyut/parlaklık) yeniden kur. */
  private gorselYenidenKur(): void {
    const kok = this.sahneGrubunaErisim();
    if (!kok) return;
    if (this.dolumGorsel && this.dolumGorselVeri) {
      const t = this.dolumZaman();
      try { this.dolumGorsel.dispose(); } catch {}
      this.dolumGorsel = new FikoDolumGorsel({
        kok, kafes: this.dolumGorselVeri.kafes, sonuc: this.dolumGorselVeri.sonuc, renkModu: 'zaman',
        envMap: this.envMapKur(), boyutFaktor: this.voxelBoyut(), parlaklik: this.voxelParlaklik(), glow: this.glowAcik(), pruzsuz: this.pruzsuzAcik(), pruzsuzKalinlik: this.pruzsuzKalinlik(),
      });
      this.dolumGorsel.zamaniAyarla(t);
    } else if (this.katilasmaGorsel && this.katilasmaGorselVeri) {
      const t = this.katilasmaZaman();
      const v = this.katilasmaGorselVeri;
      try { this.katilasmaGorsel.dispose(); } catch {}
      this.katilasmaGorsel = new FikoDolumGorsel({
        kok, kafes: v.kafes, sonuc: v.sonuc, renkModu: 'sicaklik',
        envMap: this.envMapKur(), boyutFaktor: this.voxelBoyut(), parlaklik: this.voxelParlaklik(), glow: this.glowAcik(), pruzsuz: this.pruzsuzAcik(), pruzsuzKalinlik: this.pruzsuzKalinlik(),
        sicaklikKareler: v.kareler, sicaklikMin: v.min, sicaklikMax: v.max,
      });
      this.katilasmaGorsel.zamaniAyarla(t);
    }
    this.kesitUygula();
    this.anaGosterim?.ciz?.();
  }

  /** Sensör yolluklarının ölçümlerini motordan oku + paneli/listeyi tazele */
  private sensorleriTazele(): void {
    if (!this.motor) return;
    try { this.motor.sensorleriOku(); } catch {}
    this.sogumaEgrileriHesapla();
    this.yolluklar.set([...this.motor.yolluklar()]);
    const sec = this.seciliYolluk();
    if (sec) {
      const g = this.motor.yolluklar().find(y => y.id === sec.id);
      if (g) this.seciliYolluk.set({ ...g });
    }
  }

  /** Sensör konumlarında katılaşma sıcaklık karelerinden T(t) soğuma eğrisi çıkar. */
  private sogumaEgrileriHesapla(): void {
    this.sogumaEgriMap.clear();
    const v = this.katilasmaGorselVeri;
    if (!v?.kareler?.length || v.min == null || v.max == null || !this.motor) return;
    const fark = Math.max(1, v.max - v.min);
    const kafes = v.kafes;
    for (const y of this.motor.yolluklar()) {
      if (y.tip !== 'sensor') continue;
      let lin = voxelIndeks(kafes, y.pozisyon);
      if (lin < 0 || kafes.doluluk[lin] !== 1) lin = this.enYakinDoluLin(kafes, y.pozisyon);
      if (lin < 0) continue;
      const seri = v.kareler.map((k: any) => v.min! + (k.T8[lin] / 255) * fark);
      this.sogumaEgriMap.set(y.id, seri);
    }
  }

  /** Kafeste noktaya en yakın dolu voxel (küçük komşuluk taraması). */
  private enYakinDoluLin(kafes: any, p: THREE.Vector3): number {
    const [nx, ny, nz] = kafes.boyut; const h = kafes.hucreMm;
    const ix = Math.round((p.x - kafes.origin.x) / h - 0.5);
    const iy = Math.round((p.y - kafes.origin.y) / h - 0.5);
    const iz = Math.round((p.z - kafes.origin.z) / h - 0.5);
    for (let r = 0; r <= 4; r++) {
      for (let dz = -r; dz <= r; dz++) { const z = iz + dz; if (z < 0 || z >= nz) continue;
        for (let dy = -r; dy <= r; dy++) { const yy = iy + dy; if (yy < 0 || yy >= ny) continue;
          for (let dx = -r; dx <= r; dx++) { const x = ix + dx; if (x < 0 || x >= nx) continue;
            const l = x + nx * (yy + ny * z);
            if (kafes.doluluk[l] === 1) return l;
          } } }
    }
    return -1;
  }

  /** Sensör soğuma eğrisi SVG path'i (120×40 kutu, sıcaklık ters). */
  sogumaEgrisiPath(sensorId: string): string {
    const seri = this.sogumaEgriMap.get(sensorId);
    const v = this.katilasmaGorselVeri;
    if (!seri || seri.length < 2 || !v || v.min == null || v.max == null) return '';
    const W = 120, H = 40, aralik = Math.max(1, v.max - v.min);
    let d = '';
    for (let i = 0; i < seri.length; i++) {
      const x = (i / (seri.length - 1)) * W;
      const yv = H - ((seri[i] - v.min) / aralik) * H;
      d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${Math.max(0, Math.min(H, yv)).toFixed(1)} `;
    }
    return d.trim();
  }

  sogumaEgrisiVar(sensorId: string): boolean {
    return (this.sogumaEgriMap.get(sensorId)?.length ?? 0) > 1;
  }

  /** Akış animasyonunu oynat — toplam süreyi ~4 sn gerçek-zamanda gösterir */
  dolumOynat(): void {
    if (!this.dolumGorsel || this.dolumSure() <= 0) return;
    this.dolumOynuyor.set(true);
    const sure = this.dolumSure();
    const OYNATMA_SN = 4;                          // tüm dolum ~4 sn'de oynar
    let onceki = performance.now();
    let sonSignal = 0;                             // signal güncelleme throttle (10/sn)
    this.ngZone.runOutsideAngular(() => {
      const adim = (su: number) => {
        if (!this.dolumGorsel) { this.dolumRaf = null; return; }
        const dt = (su - onceki) / 1000;
        onceki = su;
        let t = this.dolumZaman() + (dt / OYNATMA_SN) * sure;
        let bitti = false;
        if (t >= sure) { t = sure; bitti = true; }
        this.dolumGorsel.zamaniAyarla(t);
        this.anaGosterim?.ciz?.();
        // Slider takibi: ~10/sn signal güncelle
        if (su - sonSignal > 100 || bitti) {
          sonSignal = su;
          this.ngZone.run(() => { this.dolumZaman.set(t); this.CD.detectChanges(); });
        } else {
          this.dolumZaman.set(t);                  // iç değer ilerlesin (RAF içinde)
        }
        if (bitti) {
          this.dolumRaf = null;
          this.ngZone.run(() => { this.dolumOynuyor.set(false); this.CD.detectChanges(); });
          return;
        }
        this.dolumRaf = requestAnimationFrame(adim);
      };
      this.dolumRaf = requestAnimationFrame(adim);
    });
  }

  /** Animasyonu duraklat */
  dolumDuraklat(): void {
    if (this.dolumRaf != null) { cancelAnimationFrame(this.dolumRaf); this.dolumRaf = null; }
    this.dolumOynuyor.set(false);
    this.CD.detectChanges();
  }

  /** Slider'dan zaman ayarla (duraklatır) */
  dolumZamanAyarla(t: number): void {
    this.dolumDuraklat();
    const v = Math.max(0, Math.min(Number(t), this.dolumSure()));
    this.dolumZaman.set(v);
    this.dolumGorsel?.zamaniAyarla(v);
    this.anaGosterim?.ciz?.();
  }

  /** Dolum görselini ve durumu tamamen temizle */
  private dolumTemizle(): void {
    if (this.dolumRaf != null) { cancelAnimationFrame(this.dolumRaf); this.dolumRaf = null; }
    const vardiSim = !!this.dolumGorsel;
    try { this.dolumGorsel?.dispose(); } catch {}
    this.dolumGorsel = null;
    this.dokumhaneKaldir();
    this.dolumSonuc.set(null);
    this.dolumOynuyor.set(false);
    this.dolumAktif.set(false);
    this.dolumZaman.set(0);
    this.dolumSure.set(0);
    // Sim kapandıysa ısı haritasını geri getir
    const s = this.sonuc();
    if (vardiSim && s) { try { this.isiHaritasiCiz(s); } catch {} }
    this.anaGosterim?.ciz?.();
    this.CD.detectChanges();
  }

  // ════════════════════════════════════════════════════════════
  // KATILAŞMA SİMÜLASYONU — entalpi FDM: çekme/sıcak nokta/yırtılma (Faz 2)
  // ════════════════════════════════════════════════════════════

  /** Katılaşmayı başlat: önce dolumSimulasyonu'ndan kafesi al, sonra katılaşmayı
   *  o kafes üzerinde görselleştir (geç katılaşan = sıcak/kırmızı). */
  async katilasmaSimBaslat(): Promise<void> {
    this.dolumTemizle();
    this.katilasmaTemizle();
    if (!this.motor || !this.sonuc()) {
      this.nzMessage.warning('Önce "Kontrol Et" ile analiz yapın.');
      return;
    }

    this.calisiyor.set(true); this.simAsama.set('katilasma'); this.ilerleme.set(0);
    this.CD.detectChanges();
    await new Promise(r => setTimeout(r, 20));

    // Kafesi dolum simülasyonundan al (katılaşma sonucunda kafes yok)
    let ds: { kafes: any } | null = null;
    let r: IKatilasmaSonuc | null = null;
    try {
      // İlerleme: ilk %40 dolum (kafes), kalan %60 katılaşma
      ds = await this.motor.dolumSimulasyonu(this.alasimId(), (p) => { this.ilerleme.set(Math.round(p * 0.4)); this.CD.detectChanges(); });
      if (ds) {
        r = await this.motor.katilasmaSimulasyonu(this.alasimId(), this.kumId(), undefined, (p) => { this.ilerleme.set(40 + Math.round(p * 0.6)); this.CD.detectChanges(); });
      }
    } catch (e: any) {
      this.calisiyor.set(false); this.simAsama.set(null); this.CD.detectChanges();
      this.nzMessage.error('Katılaşma simülasyonu başarısız: ' + (e?.message ?? e));
      return;
    }
    this.calisiyor.set(false); this.simAsama.set(null); this.CD.detectChanges();
    if (!ds) {
      this.nzMessage.warning('Katılaşma için dolum girişi gerekli — bir dolum girişi yollugu ekleyin.');
      return;
    }
    if (!r) {
      this.nzMessage.warning('Katılaşma simülasyonu sonuç üretmedi.');
      return;
    }

    const kok = this.sahneGrubunaErisim();
    if (!kok) { this.nzMessage.error('Sahne erişimi yok.'); return; }

    // Isı haritası/etiketler görseli karıştırmasın
    this.etiketleriTemizle();
    this.isiHaritasiTemizle();

    // katilasmaSuresi'ni dolumZamani yerine sararak FikoDolumGorsel'i sıcaklık modunda kullan
    const sahteSonuc: IDolumSonuc = {
      dolumZamani: r.katilasmaSuresi,
      toplamSure: r.toplamSure,
      havaCepleri: [],
      sogukBolgeler: [],
      dolmayanSayi: 0,
    };

    try {
      this.dokumhaneKur();
      this.katilasmaGorselVeri = { kafes: ds.kafes, sonuc: sahteSonuc, kareler: r.sicaklikKareler, min: r.sicaklikMin, max: r.sicaklikMax };
      this.katilasmaGorsel = new FikoDolumGorsel({
        kok, kafes: ds.kafes, sonuc: sahteSonuc, renkModu: 'sicaklik',
        envMap: this.envMapKur(),
        boyutFaktor: this.voxelBoyut(), parlaklik: this.voxelParlaklik(), glow: this.glowAcik(), pruzsuz: this.pruzsuzAcik(), pruzsuzKalinlik: this.pruzsuzKalinlik(),
        // Gerçek sıcaklık heat-map animasyonu (soğuma dalgası) — varsa
        sicaklikKareler: r.sicaklikKareler,
        sicaklikMin: r.sicaklikMin,
        sicaklikMax: r.sicaklikMax,
        // Çekme & besleme animasyonu: besleyici seviyesi düşer, boşluk oluşur
        boslukKareler: r.boslukKareler,
      });
    } catch (e: any) {
      this.nzMessage.error('Katılaşma görseli oluşturulamadı: ' + (e?.message ?? e));
      return;
    }

    this.katilasmaSonuc.set(r);
    this.katilasmaSure.set(r.toplamSure);
    this.katilasmaZaman.set(0);
    this.katilasmaAktif.set(true);
    this.katilasmaSicaklik.set(
      r.sicaklikKareler?.length && r.sicaklikMin != null && r.sicaklikMax != null
        ? { min: r.sicaklikMin, max: r.sicaklikMax } : null,
    );
    this.katilasmaGorsel.zamaniAyarla(0);
    this.katilasmaIsaretleriCiz(r, kok);
    this.sensorleriTazele();
    this.anaGosterim?.ciz?.();
    this.CD.detectChanges();

    const ozet = r.uyarilar[0]
      ?? `Katılaşma: ${r.cekmeBolgeleri.length} çekme, ${r.sicakNoktalar.length} sıcak nokta`;
    const yst = (this.katilasmaGorsel as any)?.yuzeyStat;
    this.nzNotification.info('Katılaşma Simülasyonu', `${ozet}${this.pruzsuzAcik() && yst ? ' · Yüzey: ' + yst : ' · ' + (r.sicaklikKareler?.length ?? 0) + ' kare'}`);
    // Çekme & besleme raporu (besleyici pipe / parça boşluğu)
    if (r.parcaCekmeCm3 != null && r.parcaCekmeCm3 > 0.01) {
      this.nzNotification.warning('Çekme Boşluğu', `Parçada ${r.parcaCekmeCm3.toFixed(1)} cm³ beslenemeyen çekme boşluğu — besleyici yetersiz. Animasyonda koyu bölgeler boşluktur.`);
    } else if (r.besleyiciRapor?.length) {
      const b = r.besleyiciRapor[0];
      this.nzNotification.success('Besleme Başarılı', `Besleyici hacminin %${b.kullanilanYuzde.toFixed(0)}'i çekme telafisine gitti (pipe) — parça temiz. Animasyonda besleyici seviyesinin düşüşünü izleyin.`);
    }

    this.katilasmaOynat();
  }

  /** Katılaşma animasyonunu oynat — toplam süreyi ~6 sn'de gösterir */
  katilasmaOynat(): void {
    if (!this.katilasmaGorsel || this.katilasmaSure() <= 0) return;
    this.katilasmaOynuyor.set(true);
    const sure = this.katilasmaSure();
    const OYNATMA_SN = 6;
    let onceki = performance.now();
    let sonSignal = 0;
    this.ngZone.runOutsideAngular(() => {
      const adim = (su: number) => {
        if (!this.katilasmaGorsel) { this.katilasmaRaf = null; return; }
        const dt = (su - onceki) / 1000;
        onceki = su;
        let t = this.katilasmaZaman() + (dt / OYNATMA_SN) * sure;
        let bitti = false;
        if (t >= sure) { t = sure; bitti = true; }
        this.katilasmaGorsel.zamaniAyarla(t);
        this.anaGosterim?.ciz?.();
        if (su - sonSignal > 100 || bitti) {
          sonSignal = su;
          this.ngZone.run(() => { this.katilasmaZaman.set(t); this.CD.detectChanges(); });
        } else {
          this.katilasmaZaman.set(t);
        }
        if (bitti) {
          this.katilasmaRaf = null;
          this.ngZone.run(() => { this.katilasmaOynuyor.set(false); this.CD.detectChanges(); });
          return;
        }
        this.katilasmaRaf = requestAnimationFrame(adim);
      };
      this.katilasmaRaf = requestAnimationFrame(adim);
    });
  }

  /** Katılaşma animasyonunu duraklat */
  katilasmaDuraklat(): void {
    if (this.katilasmaRaf != null) { cancelAnimationFrame(this.katilasmaRaf); this.katilasmaRaf = null; }
    this.katilasmaOynuyor.set(false);
    this.CD.detectChanges();
  }

  /** Slider'dan katılaşma zamanı ayarla (duraklatır) */
  katilasmaZamanAyarla(t: number): void {
    this.katilasmaDuraklat();
    const v = Math.max(0, Math.min(Number(t), this.katilasmaSure()));
    this.katilasmaZaman.set(v);
    this.katilasmaGorsel?.zamaniAyarla(v);
    this.anaGosterim?.ciz?.();
  }

  /** Defekt işaretleri: çekme (kırmızı), beslenmemiş sıcak nokta (kırmızı belirgin),
   *  beslenen sıcak nokta (turuncu), sıcak yırtılma (mor) için küçük sahne küreleri. */
  private katilasmaIsaretleriCiz(r: IKatilasmaSonuc, kok: THREE.Object3D): void {
    this.katilasmaIsaretleriKaldir();
    const koseUz = this.sonuc()?.geo?.bbox?.getSize(this._v).length() ?? 0;
    const yari = koseUz > 0 ? koseUz * 0.012 : 1.2;
    const ekle = (merkez: THREE.Vector3, renk: number, olcek: number) => {
      try {
        const m = new THREE.Mesh(
          new THREE.SphereGeometry(yari * olcek, 12, 8),
          new THREE.MeshBasicMaterial({ color: renk, depthTest: false, transparent: true, opacity: 0.92 }),
        );
        m.position.copy(merkez);
        m.renderOrder = 999;
        kok.add(m);
        this.katilasmaIsaretleri.push(m);
      } catch {}
    };
    for (const c of r.cekmeBolgeleri) ekle(c.merkez, 0xef4444, 1);
    for (const s of r.sicakNoktalar) ekle(s.merkez, s.beslendi ? 0xf97316 : 0xef4444, s.beslendi ? 1 : 1.6);
    for (const y of r.yirtilmaBolgeleri) ekle(y.merkez, 0xa855f7, 0.9);
  }

  private katilasmaIsaretleriKaldir(): void {
    const kok = this.sahneGrubunaErisim();
    for (const m of this.katilasmaIsaretleri) {
      try { kok?.remove(m); } catch {}
      try { m.geometry.dispose(); (m.material as THREE.Material).dispose(); } catch {}
    }
    this.katilasmaIsaretleri = [];
  }

  /** Katılaşma görselini ve durumu tamamen temizle */
  private katilasmaTemizle(): void {
    if (this.katilasmaRaf != null) { cancelAnimationFrame(this.katilasmaRaf); this.katilasmaRaf = null; }
    const vardiSim = !!this.katilasmaGorsel;
    try { this.katilasmaGorsel?.dispose(); } catch {}
    this.katilasmaGorsel = null;
    this.dokumhaneKaldir();
    this.katilasmaIsaretleriKaldir();
    this.katilasmaSonuc.set(null);
    this.katilasmaOynuyor.set(false);
    this.katilasmaAktif.set(false);
    this.katilasmaZaman.set(0);
    this.katilasmaSure.set(0);
    this.katilasmaSicaklik.set(null);
    // Sim kapandıysa ısı haritasını geri getir
    const s = this.sonuc();
    if (vardiSim && s) { try { this.isiHaritasiCiz(s); } catch {} }
    this.anaGosterim?.ciz?.();
    this.CD.detectChanges();
  }

  // ════════════════════════════════════════════════════════════
  // YOLLUK — manuel ekleme / kabul / yenileme
  // ════════════════════════════════════════════════════════════

  yollukTipSec(t: YollukTipi): void {
    if (this.aktifYollukTip() === t) {
      this.aktifYollukTip.set(null);
      this.tiklamaModu.set(null);
    } else {
      this.aktifYollukTip.set(t);
      this.tiklamaModu.set(t);
    }
  }

  private yolluklariYenile(): void {
    if (!this.motor) return;
    const liste = this.motor.yolluklar();
    this.yolluklar.set([...liste]);
    // 3D objeleri düzenleyici motoruna kur/güncelle/sil
    this.duzenleyici?.senkronize(liste);
    // Seçili eleman silindiyse paneli temizle, yoksa tazele
    const seciliId = this.seciliYolluk()?.id;
    if (seciliId) {
      const hala = liste.find(y => y.id === seciliId);
      this.seciliYolluk.set(hala ? { ...hala } : null);
      if (!hala) this.duzenleyici?.sec(null);
    }
    // Yolluk kapsaması → bölge çözüm durumları + not güncellensin (voxel YOK, ucuz)
    this.kapsamaTazele();
    // Yolluk değişimi dolum/verim hesabını etkiler → methoding'i tazele
    this.methodingYenile();
    this.anaGosterim?.ciz?.();
    this.CD.detectChanges();
  }

  /** Yolluk kapsamasını yeniden değerlendir → bölge çözüm rozetleri + not + etiketler tazelensin */
  private kapsamaTazele(): void {
    const mevcut = this.sonuc();
    if (!this.motor || !mevcut) return;
    const k = this.motor.yollukKapsamaGuncelle();
    if (!k) return;
    // bolgeler yerinde mutasyona uğradı (cozuldu/cozumMesaji); yeni not ile sonuç objesini tazele
    this.sonuc.set({ ...mevcut, not: k.not });
    const secili = this.seciliBolge();
    if (secili) {
      const guncel = k.gosterge.bolgeler.find(b => b.id === secili.id);
      if (guncel) this.seciliBolge.set(guncel);
    }
    // Etiketleri yeniden kur (çözülen yeşil ✓) — dolum/katılaşma görseli aktif değilse
    if (!this.dolumGorsel && !this.katilasmaGorsel) {
      this.sahneEtiketleriKur(k.gosterge.bolgeler);
    }
  }

  // ════════════════════════════════════════════════════════════
  // SAHNE → MESH TOPLAMA (wall-thickness deseni)
  // ════════════════════════════════════════════════════════════

  private aktifMeshleriTopla(): THREE.Mesh[] {
    const meshler: THREE.Mesh[] = [];
    if (!this.anaGosterim) return meshler;

    // 1) Seçili gruplar
    const gruplar: any[] = [];
    const sec = this.anaGosterim.secililer;
    if (sec?.grupSecimlerUzerinde) gruplar.push(sec.grupSecimlerUzerinde);
    if (sec?.grupSecimler2Uzerinde) gruplar.push(sec.grupSecimler2Uzerinde);
    gruplar.forEach(grup => {
      grup?.children?.forEach((c: any) => {
        if (c?.mesh instanceof THREE.Mesh) meshler.push(c.mesh);
      });
    });

    // 2) Seçim yoksa tüm görünür mesh'ler
    if (meshler.length === 0 && this.anaGosterim.sahneler?.[0]) {
      this.anaGosterim.sahneler[0].traverse((c: any) => {
        if (c?.mesh instanceof THREE.Mesh && c.mesh.visible) meshler.push(c.mesh);
      });
    }
    return meshler;
  }

  // ════════════════════════════════════════════════════════════
  // SAHNE ETİKETLERİ (CSS2DObject — gerçek CAD callout)
  // ════════════════════════════════════════════════════════════

  /** Bölgeleri sadeleştir: tip başına en kritik (siddet) birkaçı, toplam tavanla */
  private etiketlenecekBolgeler(bolgeler: IDokumBolge[]): IDokumBolge[] {
    const tipBazli = new Map<GostergeTipi, IDokumBolge[]>();
    for (const b of bolgeler) {
      const liste = tipBazli.get(b.tip) ?? [];
      liste.push(b);
      tipBazli.set(b.tip, liste);
    }
    const secilenler: IDokumBolge[] = [];
    for (const liste of tipBazli.values()) {
      liste.sort((a, c) => (c.siddet ?? 0) - (a.siddet ?? 0));
      secilenler.push(...liste.slice(0, FikoDokumKontrolComponent.ETIKET_TIP_LIMIT));
    }
    // Toplam tavan: en kritikten başla
    secilenler.sort((a, c) => (c.siddet ?? 0) - (a.siddet ?? 0));
    return secilenler.slice(0, FikoDokumKontrolComponent.ETIKET_TOPLAM_LIMIT);
  }

  /** Etiket değerini makul biçimlendir — oran (x) en fazla ">10×" ile kırpılır */
  private etiketDeger(b: IDokumBolge): string {
    if (b.birim === 'x' || b.birim === '×') {
      return b.deger > 10 ? '>10×' : `${b.deger.toFixed(1)}×`;
    }
    return `${b.deger.toFixed(1)}${b.birim}`;
  }

  /** Sahneye gömülü CSS2D etiketleri kur (orbit ile parçayla hareket eder) */
  private sahneEtiketleriKur(bolgeler: IDokumBolge[]): void {
    this.etiketleriTemizle();
    if (this.etiketModu() === 'gizli') return;

    const kok = this.anaGosterim?.sahneler?.[0] as THREE.Scene | undefined;
    if (!(kok instanceof THREE.Object3D)) return;   // CSS2DRenderer bu sahneyi render eder

    const hedefler = this.etiketlenecekBolgeler(bolgeler);
    const baslangicGorunur = this.etiketModu() === 'hepsi';

    // Parça boyutuna oranlı işaret/ofset (bbox köşegeni) — yoksa makul sabit
    const kose = this.sonuc()?.geo?.bbox?.getSize(this._v).length() ?? 0;
    const olcek = kose > 0 ? kose * 0.04 : 4;
    const isaretYari = Math.max(olcek * 0.18, 0.4);

    for (const b of hedefler) {
      const renk = this.bolgeRenk(b);
      try {
        // Etiket merkez üzerinde küçük yukarı ofsette dursun → lider çizgi görünür
        const etiketPoz = b.merkez.clone();
        etiketPoz.y += olcek;

        // ── HTML label ──
        const div = document.createElement('div');
        div.className = b.cozuldu ? 'dk-sahne-etiket cozuldu' : 'dk-sahne-etiket';
        div.style.setProperty('--dk-se-renk', renk);
        div.style.opacity = baslangicGorunur ? '1' : '0';
        const nokta = document.createElement('span'); nokta.className = 'nokta'; if (b.cozuldu) nokta.textContent = '✓';
        const metin = document.createElement('span'); metin.textContent = b.baslik;
        const deger = document.createElement('span'); deger.className = 'deger'; deger.textContent = this.etiketDeger(b);
        div.append(nokta, metin, deger);
        div.addEventListener('pointerdown', (ev) => { ev.stopPropagation(); this.bolgeyeUc(b); });

        const obj = new CSS2DObject(div);
        obj.position.copy(etiketPoz);

        // ── Sahnede küçük işaret küresi (bölge merkezinde) ──
        const isaret = new THREE.Mesh(
          new THREE.SphereGeometry(isaretYari, 12, 8),
          new THREE.MeshBasicMaterial({ color: new THREE.Color(renk), depthTest: false, transparent: true }),
        );
        isaret.position.copy(b.merkez);
        isaret.renderOrder = 998;

        // ── Lider çizgi (bölge merkezi → etiket) ──
        const cizgiGeo = new THREE.BufferGeometry().setFromPoints([b.merkez.clone(), etiketPoz.clone()]);
        const cizgi = new THREE.Line(cizgiGeo, new THREE.LineBasicMaterial({ color: new THREE.Color(renk), transparent: true, opacity: 0.55, depthTest: false }));
        cizgi.renderOrder = 997;

        kok.add(obj, isaret, cizgi);
        this.sahneEtiketObjeleri.push({ bolgeId: b.id, obj, div, isaret, cizgi });
      } catch {}
    }
    this.anaGosterim?.ciz?.();
  }

  /** Tüm sahne etiketlerini sahneden çıkar + dispose et */
  private etiketleriTemizle(): void {
    const kok = this.anaGosterim?.sahneler?.[0] as THREE.Object3D | undefined;
    for (const e of this.sahneEtiketObjeleri) {
      try { kok?.remove(e.obj, e.isaret, e.cizgi); } catch {}
      try { e.obj.element?.parentNode?.removeChild(e.obj.element); } catch {}
      try { e.isaret.geometry.dispose(); (e.isaret.material as THREE.Material).dispose(); } catch {}
      try { e.cizgi.geometry.dispose(); (e.cizgi.material as THREE.Material).dispose(); } catch {}
    }
    this.sahneEtiketObjeleri = [];
    this.anaGosterim?.ciz?.();
  }

  /** Etiket görünüm modu (hepsi/hover/secim/gizli) için div opacity'lerini güncelle */
  private etiketGorunurlukTazele(): void {
    const mod = this.etiketModu();
    const hover = this.hoverBolgeId();
    const secili = this.seciliBolge()?.id;
    for (const e of this.sahneEtiketObjeleri) {
      let gorunur = false;
      switch (mod) {
        case 'hepsi': gorunur = true; break;
        case 'gizli': gorunur = false; break;
        case 'hover': gorunur = e.bolgeId === hover || e.bolgeId === secili; break;
        case 'secim': gorunur = e.bolgeId === secili; break;
      }
      e.div.style.opacity = gorunur ? '1' : '0';
      e.div.style.pointerEvents = gorunur ? 'auto' : 'none';
    }
  }

  // ════════════════════════════════════════════════════════════
  // ISI HARİTASI (vertex color)
  // ════════════════════════════════════════════════════════════

  private isiHaritasiCiz(sonuc: IKontrolSonuc): void {
    this.isiHaritasiTemizle();
    const meshler = this.aktifMeshleriTopla();
    if (meshler.length === 0) return;

    const positions = sonuc.geo?.positions;             // dünya uzayı, 3/vertex (birleşik geo)
    const kalinlik = sonuc.gosterge?.kalinlikAlani;     // per-vertex kalınlık (birleşik geo ile hizalı)

    // bbox kenarına göre quantize toleransı (~1e-5) — aynı dünya-konumlu
    // vertex'ler tek anahtara düşer → komşu üçgenler eşleşir → pürüzsüz gradyan
    const bbox = sonuc.geo?.bbox ?? new THREE.Box3();
    const koseUz = bbox.isEmpty() ? 0 : bbox.getSize(this._v).length();
    const kuant = Math.max(koseUz * 1e-5, 1e-6);

    // ── Pozisyona göre kalınlık tablosu (kaynaklama anahtarı) ──
    const konumKalinlik = new Map<string, number>();
    if (positions && kalinlik && kalinlik.length * 3 >= positions.length) {
      const n = Math.min(kalinlik.length, positions.length / 3);
      for (let i = 0; i < n; i++) {
        const k = this.konumAnahtar(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2], kuant);
        // Aynı konumda birden çok kayıt varsa en küçük (en ince) kalınlığı tut
        const v = kalinlik[i];
        const eski = konumKalinlik.get(k);
        if (eski === undefined || v < eski) konumKalinlik.set(k, v);
      }
    }

    // ── Persantil clamp (%5–%95) — tüm parça kırmızıya boyanmasın ──
    let dusuk = sonuc.gosterge?.minKalinlik ?? 0;
    let yuksek = sonuc.gosterge?.maxKalinlik ?? 1;
    if (kalinlik && kalinlik.length > 4) {
      const sirali = Array.from(kalinlik).filter(v => isFinite(v) && v > 0).sort((a, b) => a - b);
      if (sirali.length > 4) {
        dusuk = sirali[Math.floor(sirali.length * 0.05)];
        yuksek = sirali[Math.floor(sirali.length * 0.95)];
      }
    }
    if (!(yuksek > dusuk)) yuksek = dusuk + 1;

    const tablolu = konumKalinlik.size > 0;
    const renkVec = new THREE.Color();
    const dunyaP = new THREE.Vector3();

    for (const mesh of meshler) {
      const geo = mesh.geometry as THREE.BufferGeometry;
      const pos = geo.getAttribute('position');
      if (!pos) continue;

      this.orijinalRenkler.set(mesh, geo.getAttribute('color') as THREE.BufferAttribute | undefined);

      const renkler = new Float32Array(pos.count * 3);
      mesh.updateWorldMatrix(true, false);
      const m = mesh.matrixWorld;

      for (let vi = 0; vi < pos.count; vi++) {
        dunyaP.set(pos.getX(vi), pos.getY(vi), pos.getZ(vi)).applyMatrix4(m);
        let oran: number;
        if (tablolu) {
          const k = this.konumAnahtar(dunyaP.x, dunyaP.y, dunyaP.z, kuant);
          const t = konumKalinlik.get(k);
          // Eşleşme yoksa nötr orta değer (gradyan kopması yerine yumuşak geçiş)
          oran = t === undefined ? 0.5 : (t - dusuk) / (yuksek - dusuk);
        } else {
          oran = 0.5;
        }
        this.isiRengi(Math.min(1, Math.max(0, oran)), renkVec);
        renkler[vi * 3] = renkVec.r;
        renkler[vi * 3 + 1] = renkVec.g;
        renkler[vi * 3 + 2] = renkVec.b;
      }

      geo.setAttribute('color', new THREE.BufferAttribute(renkler, 3));
      // Pürüzsüz gölgeleme → süreksiz/üçgensel görünümü engelle
      try { geo.computeVertexNormals(); } catch {}

      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (mat && 'vertexColors' in mat) {
        (mat as any).__dkVertexColorsOnceki = mat.vertexColors;
        (mat as any).__dkFlatOnceki = (mat as any).flatShading;
        mat.vertexColors = true;
        (mat as any).flatShading = false;
        mat.needsUpdate = true;
      }
      this.boyaliMeshler.add(mesh);
    }
  }

  /** Dünya konumunu quantize edip string anahtara çevir (kaynaklama için) */
  private konumAnahtar(x: number, y: number, z: number, kuant: number): string {
    const q = 1 / kuant;
    return `${Math.round(x * q)}_${Math.round(y * q)}_${Math.round(z * q)}`;
  }

  /** İnce→kalın için mavi→yeşil→sarı→kırmızı yumuşak gradyan (oran 0..1) */
  private isiRengi(oran: number, hedef: THREE.Color): THREE.Color {
    // 0=ince(mavi) … 1=kalın(kırmızı). HSL ile sürekli geçiş: 220° → 0°
    const ton = (1 - oran) * 220 / 360;   // mavi(0.61) → kırmızı(0)
    return hedef.setHSL(ton, 0.85, 0.52);
  }

  private isiHaritasiTemizle(): void {
    for (const mesh of this.boyaliMeshler) {
      const geo = mesh.geometry as THREE.BufferGeometry;
      const onceki = this.orijinalRenkler.get(mesh);
      if (onceki) geo.setAttribute('color', onceki);
      else geo.deleteAttribute('color');
      const mat = mesh.material as any;
      if (mat && '__dkVertexColorsOnceki' in mat) {
        mat.vertexColors = mat.__dkVertexColorsOnceki;
        delete mat.__dkVertexColorsOnceki;
        if ('__dkFlatOnceki' in mat) {
          mat.flatShading = mat.__dkFlatOnceki;
          delete mat.__dkFlatOnceki;
        }
        mat.needsUpdate = true;
      }
    }
    this.boyaliMeshler.clear();
    this.orijinalRenkler.clear();
    if (this.isiHaritasiGrup) {
      this.sahneGrubunaErisim()?.remove(this.isiHaritasiGrup);
      this.isiHaritasiGrup = null;
    }
  }

  // ════════════════════════════════════════════════════════════
  // SAHNE TIKLAMASI — yolluk ekleme + yolluk kabul + bölge seçimi
  // ════════════════════════════════════════════════════════════

  private tiklamaModunuKur(): void {
    const canvas = this.anaGosterim?.canvas?.nativeElement as HTMLElement | undefined;
    if (!canvas) return;

    this.tiklamaDinleyici = (e: PointerEvent) => {
      if (e.button !== 0) return;

      // ── 3D'de döküm yönü seçme: parçaya tıkla, o yüzey "üst" olsun ──
      if (this.yon3dSecModu()) {
        const vurus = this.raycastVurus(e);
        if (vurus && this.motor) {
          this.motor.yukariYonAyarla(vurus.normal.clone());
          this.yukariYon.set('3D');
          this.yon3dSecModu.set(false);
          this.nzMessage.success('Döküm yönü ayarlandı — yeniden analiz ediliyor');
          this.kontrolEt();
        }
        return;
      }

      const tip = this.tiklamaModu();

      // ── Yerleştirme modu AÇIK → eskisi gibi yolluk ekle ──
      if (tip) {
        // Dolum girişi için modlu yön belirleme akışı
        if (tip === 'dolum_girisi') {
          this.dolumGirisiPointerDown(e);
          return;
        }
        // Diğer tipler: tek-tık (yön varsayılan +Y)
        const nokta = this.raycastNokta(e);
        if (nokta && this.motor) {
          try {
            this.motor.yollukEkle(tip, nokta);
            this.yolluklariYenile();
          } catch (err: any) {
            this.nzMessage.error('Yolluk eklenemedi: ' + (err?.message ?? err));
          }
        }
        return;
      }

      // ── Yerleştirme modu KAPALI → seçim / kabul ──
      // Gizmo sürüklemesi sürüyorsa dokunma
      if (this.duzenleyici?.gizmoMesgul()) return;

      // Mevcut bir yolluk objesine tıklandı mı?
      const id = this.duzenleyici?.elemanSecPointer(e) ?? null;
      if (id) {
        this.duzenleyici!.sec(id);
        const el = this.motor?.yolluklar().find(y => y.id === id) ?? null;
        this.seciliYolluk.set(el);
        this.CD.detectChanges();
        return;
      }

      // Yolluk yok → hayalet kabul dene
      if (this.yollukKabulDene(e)) return;

      // Hiçbiri → seçimi bırak
      this.duzenleyici?.sec(null);
      this.seciliYolluk.set(null);
      this.CD.detectChanges();
    };
    canvas.addEventListener('pointerdown', this.tiklamaDinleyici);

    // İki-tık önizleme oku ve sürükle için pointermove/up
    this.tiklamaMoveDinleyici = (e: PointerEvent) => this.dolumGirisiPointerMove(e);
    this.tiklamaUpDinleyici = (e: PointerEvent) => this.dolumGirisiPointerUp(e);
    canvas.addEventListener('pointermove', this.tiklamaMoveDinleyici);
    window.addEventListener('pointerup', this.tiklamaUpDinleyici);

    this.klavyeDinleyici = (e: KeyboardEvent) => {
      // Input/textarea'ya yazarken yolluk kısayollarını tetikleme
      const hedef = e.target as HTMLElement | null;
      if (hedef && (hedef.tagName === 'INPUT' || hedef.tagName === 'TEXTAREA' || hedef.isContentEditable)) return;

      // Enter: sürükle düzenlemesini onayla
      if (e.key === 'Enter' && this.duzenlenenGirisId) {
        this.girisDuzenlemeOnayla();
        return;
      }
      // Delete/Backspace: seçili yolluğu sil (yerleştirme modu kapalıyken)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !this.tiklamaModu()) {
        const secili = this.seciliYolluk();
        if (secili) {
          e.preventDefault();
          this.yollukSilTikla(secili);
          return;
        }
      }
      if (e.key === 'Escape') {
        // Bekleyen/düzenleme durumlarını öncelikli temizle
        if (this.bekleyenGiris || this.duzenlenenGirisId || this.onizlemeOk) {
          this.girisDuzenlemeIptal();
          return;
        }
        if (this.yon3dSecModu()) { this.yon3dSecModu.set(false); this.CD.detectChanges(); return; }
        if (this.tiklamaModu()) {
          this.tiklamaModu.set(null);
          this.aktifYollukTip.set(null);
          this.CD.detectChanges();
          return;
        }
        if (this.seciliYolluk()) { this.yollukSecimBirak(); return; }
        if (this.seciliBolge()) { this.detayKapat(); this.CD.detectChanges(); return; }
        // Hiçbir alt-mod aktif değil → HUD'u kapat
        this.ngZone.run(() => this.kapat.emit());
      }
    };
    window.addEventListener('keydown', this.klavyeDinleyici);
  }

  private tiklamaModuTemizle(): void {
    const canvas = this.anaGosterim?.canvas?.nativeElement as HTMLElement | undefined;
    if (canvas && this.tiklamaDinleyici) canvas.removeEventListener('pointerdown', this.tiklamaDinleyici);
    if (canvas && this.tiklamaMoveDinleyici) canvas.removeEventListener('pointermove', this.tiklamaMoveDinleyici);
    if (this.tiklamaUpDinleyici) window.removeEventListener('pointerup', this.tiklamaUpDinleyici);
    if (this.klavyeDinleyici) window.removeEventListener('keydown', this.klavyeDinleyici);
    this.tiklamaDinleyici = null;
    this.tiklamaMoveDinleyici = null;
    this.tiklamaUpDinleyici = null;
    this.klavyeDinleyici = null;
  }

  // ════════════════════════════════════════════════════════════
  // DOLUM GİRİŞİ — modlu yön belirleme (otomatik / iki-tık / sürükle)
  // ════════════════════════════════════════════════════════════

  private dolumGirisiPointerDown(e: PointerEvent): void {
    if (!this.motor) return;
    const vurus = this.raycastVurus(e);
    if (!vurus) return;

    const mod = this.girisYonModu();

    if (mod === 'otomatik') {
      // Yüzey normalinden içeri (metalin akış yönü)
      const yon = vurus.normal.clone().negate().normalize();
      try {
        this.motor.yollukEkle('dolum_girisi', vurus.nokta, yon);
        this.yolluklariYenile();
      } catch (err: any) {
        this.nzMessage.error('Yolluk eklenemedi: ' + (err?.message ?? err));
      }
      return;
    }

    if (mod === 'iki_tik') {
      if (!this.bekleyenGiris) {
        // 1. tık → konum + normal sakla, önizleme okunu kur
        this.bekleyenGiris = { pos: vurus.nokta.clone(), normal: vurus.normal.clone() };
        this.bekleyenYon = vurus.normal.clone().negate();
        this.onizlemeOkKur(vurus.nokta, vurus.normal.clone().negate());
        this.girisDuzenlemeAktif.set(true);
      } else {
        // 2. tık → yön = (hedef - pos)
        const yon = vurus.nokta.clone().sub(this.bekleyenGiris.pos);
        if (yon.lengthSq() < 1e-8) yon.copy(this.bekleyenGiris.normal).negate();
        yon.normalize();
        try {
          this.motor.yollukEkle('dolum_girisi', this.bekleyenGiris.pos.clone(), yon);
          this.yolluklariYenile();
        } catch (err: any) {
          this.nzMessage.error('Yolluk eklenemedi: ' + (err?.message ?? err));
        }
        this.girisDuzenlemeIptal(); // bekleyeni + önizleme okunu temizle
      }
      return;
    }

    // surukle: hemen ekle, id'yi düzenlemeye al
    if (mod === 'surukle') {
      try {
        const eleman = this.motor.yollukEkle('dolum_girisi', vurus.nokta, vurus.normal.clone().negate().normalize());
        this.yolluklariYenile();
        this.duzenlenenGirisId = eleman?.id ?? null;
        this.surukluyor = true;
        this.girisDuzenlemeAktif.set(true);
      } catch (err: any) {
        this.nzMessage.error('Yolluk eklenemedi: ' + (err?.message ?? err));
      }
    }
  }

  private dolumGirisiPointerMove(e: PointerEvent): void {
    const mod = this.girisYonModu();
    if (this.tiklamaModu() !== 'dolum_girisi') return;

    // İki-tık: bekleyen varken önizleme okunu güncelle
    if (mod === 'iki_tik' && this.bekleyenGiris && this.onizlemeOk) {
      const hedef = this.raycastNokta(e);
      const yon = hedef ? hedef.clone().sub(this.bekleyenGiris.pos) : null;
      if (yon && yon.lengthSq() > 1e-8) {
        const uzunluk = yon.length();
        yon.normalize();
        this.bekleyenYon = yon.clone();
        this.onizlemeOk.position.copy(this.bekleyenGiris.pos);
        this.onizlemeOk.setDirection(yon);
        this.onizlemeOk.setLength(uzunluk, uzunluk * 0.25, uzunluk * 0.15);
        this.anaGosterim?.ciz?.();
      }
      return;
    }

    // Sürükle: basılıyken canlı yön güncelle
    if (mod === 'surukle' && this.surukluyor && this.duzenlenenGirisId && this.motor) {
      const eleman = this.yolluklar().find(y => y.id === this.duzenlenenGirisId);
      if (!eleman) return;
      const hedef = this.raycastNokta(e);
      if (!hedef) return;
      const yon = hedef.clone().sub(eleman.pozisyon);
      if (yon.lengthSq() < 1e-8) return;
      yon.normalize();
      try {
        this.motor.yollukYonGuncelle(this.duzenlenenGirisId, yon);
        this.yolluklariYenile();
      } catch {}
    }
  }

  private dolumGirisiPointerUp(_e: PointerEvent): void {
    if (this.girisYonModu() === 'surukle' && this.surukluyor && this.duzenlenenGirisId) {
      // Drag bitti → düzenlemeyi onayla
      this.girisDuzenlemeOnayla();
    }
  }

  /** Geçici önizleme okunu sahneye kur */
  private onizlemeOkKur(pos: THREE.Vector3, yon: THREE.Vector3): void {
    const kok = this.sahneGrubunaErisim();
    if (!kok) return;
    this.onizlemeOkKaldir();
    const d = yon.clone().normalize();
    const ok = new THREE.ArrowHelper(d, pos, 20, new THREE.Color(YOLLUK_RENK['dolum_girisi']).getHex(), 6, 4);
    ok.renderOrder = 999;
    this.onizlemeOk = ok;
    kok.add(ok);
    this.anaGosterim?.ciz?.();
  }

  private onizlemeOkKaldir(): void {
    if (this.onizlemeOk) {
      this.sahneGrubunaErisim()?.remove(this.onizlemeOk);
      try { this.onizlemeOk.dispose?.(); } catch {}
      this.onizlemeOk = null;
    }
  }

  /** Sürükle düzenlemesini bitir (onayla) */
  private girisDuzenlemeOnayla(): void {
    this.duzenlenenGirisId = null;
    this.surukluyor = false;
    this.bekleyenYon = null;
    this.girisDuzenlemeAktif.set(false);
    this.anaGosterim?.ciz?.();
    this.CD.detectChanges();
  }

  /** Bekleyen iki-tık / sürükle düzenlemesini iptal et + önizleme okunu temizle.
   *  Sürüklemede henüz onaylanmamış elemanı kaldırır. */
  private girisDuzenlemeIptal(): void {
    // Sürükle modunda iptal → son ekleneni sil
    if (this.duzenlenenGirisId && this.motor) {
      try { this.motor.yollukSil(this.duzenlenenGirisId); } catch {}
      this.yolluklariYenile();
    }
    this.bekleyenGiris = null;
    this.bekleyenYon = null;
    this.duzenlenenGirisId = null;
    this.surukluyor = false;
    this.girisDuzenlemeAktif.set(false);
    this.onizlemeOkKaldir();
    this.CD.detectChanges();
  }

  /** Görünür "Onayla" butonu: bekleyen iki-tık/sürükle düzenlemesini tamamlar. */
  girisOnayla(): void {
    const mod = this.girisYonModu();
    if (mod === 'iki_tik' && this.bekleyenGiris && this.motor) {
      const yon = (this.bekleyenYon && this.bekleyenYon.lengthSq() > 1e-8)
        ? this.bekleyenYon.clone()
        : this.bekleyenGiris.normal.clone().negate();
      try {
        this.motor.yollukEkle('dolum_girisi', this.bekleyenGiris.pos.clone(), yon);
        this.yolluklariYenile();
      } catch (err: any) {
        this.nzMessage.error('Yolluk eklenemedi: ' + (err?.message ?? err));
      }
      this.bekleyenGiris = null;
      this.bekleyenYon = null;
      this.girisDuzenlemeAktif.set(false);
      this.onizlemeOkKaldir();
      this.CD.detectChanges();
      return;
    }
    // sürükle veya diğer: mevcut konum/yön korunarak onayla
    this.girisDuzenlemeOnayla();
  }

  /** Görünür "İptal" butonu. */
  girisIptal(): void {
    this.girisDuzenlemeIptal();
  }

  /** Görünür "Bitir" butonu: yerleştirme modundan çık (varsa bekleyen düzenlemeyi iptal et). */
  yerlestirmeyiBitir(): void {
    if (this.girisDuzenlemeAktif()) this.girisDuzenlemeIptal();
    this.tiklamaModu.set(null);
    this.aktifYollukTip.set(null);
    this.CD.detectChanges();
  }

  /** Tüm giriş yön durumunu (ngOnDestroy) temizle — eleman silmeden */
  private girisYonDurumTemizle(): void {
    this.bekleyenGiris = null;
    this.bekleyenYon = null;
    this.duzenlenenGirisId = null;
    this.surukluyor = false;
    this.girisDuzenlemeAktif.set(false);
    this.onizlemeOkKaldir();
  }

  private yollukKabulDene(e: PointerEvent): boolean {
    if (!this.motor || !this.duzenleyici) return false;
    const id = this.duzenleyici.elemanSecPointer(e);
    if (!id) return false;
    const eleman = this.yolluklar().find(y => y.id === id);
    if (eleman && !eleman.kabulEdildi) {
      try {
        this.motor.yollukKabul(id);
        this.yolluklariYenile();
        this.nzMessage.success('Yolluk kabul edildi.');
      } catch {}
      return true;
    }
    return false;
  }

  private raycastNokta(e: PointerEvent): THREE.Vector3 | null {
    return this.raycastVurus(e)?.nokta ?? null;
  }

  /** Yüzeye raycast: hit noktası + dünya-uzayı normali */
  private raycastVurus(e: PointerEvent): { nokta: THREE.Vector3; normal: THREE.Vector3 } | null {
    const kamera = this.kameraAl();
    const kok = this.sahneGrubunaErisim();
    if (!kamera || !kok) return null;
    this.fareNormalize(e);
    this.raycaster.setFromCamera(this._fare, kamera);
    const meshler = this.aktifMeshleriTopla();
    const hits = this.raycaster.intersectObjects(meshler, true);
    if (hits.length === 0) return null;
    const hit = hits[0];
    const nokta = hit.point.clone();

    // Yüz normalini dünya uzayına çevir (normalMatrix ile), yoksa +Y fallback
    let normal = new THREE.Vector3(0, 1, 0);
    if (hit.face && hit.object) {
      const nm = new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld);
      normal = hit.face.normal.clone().applyMatrix3(nm).normalize();
    }
    return { nokta, normal };
  }

  private fareNormalize(e: PointerEvent): void {
    const canvas = this.anaGosterim?.canvas?.nativeElement as HTMLElement;
    const r = canvas.getBoundingClientRect();
    this._fare.set(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1,
    );
  }

  // ════════════════════════════════════════════════════════════
  // KAMERA UÇURMA
  // ════════════════════════════════════════════════════════════

  bolgeyeUc(bolge: IDokumBolge): void {
    this.seciliBolge.set(bolge);
    this.etiketGorunurlukTazele();
    this.kameraylaOdakla(bolge.merkez);
    this.CD.detectChanges();
  }

  /** Kamerayı (OrbitControls hedefini) verilen dünya noktasına yumuşakça odakla */
  private kameraylaOdakla(merkez: THREE.Vector3): void {
    const kamera = this.kameraAl();
    const kontrol = this.anaGosterim?.aktifKontrol;
    if (!kamera || !merkez) return;

    const hedef = merkez.clone();

    // OrbitControls varsa: target'ı yumuşakça hedefe taşı (kamera mesafesini koru)
    if (kontrol && kontrol.target) {
      const baslangicTarget = kontrol.target.clone();
      const baslangicPoz = kamera.position.clone();
      const ofset = baslangicPoz.clone().sub(baslangicTarget); // yön+mesafe korunur
      const sure = 600;
      const t0 = performance.now();
      const ease = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const adim = () => {
        const t = Math.min((performance.now() - t0) / sure, 1);
        const k = ease(t);
        kontrol.target.lerpVectors(baslangicTarget, hedef, k);
        kamera.position.copy(kontrol.target).add(ofset);
        kontrol.update?.();
        this.anaGosterim?.ciz?.();
        if (t < 1) requestAnimationFrame(adim);
      };
      requestAnimationFrame(adim);
    } else {
      // Fallback: basit lookAt
      kamera.lookAt?.(hedef);
      this.anaGosterim?.ciz?.();
    }
    this.CD.detectChanges();
  }

  // ════════════════════════════════════════════════════════════
  // YARDIMCILAR
  // ════════════════════════════════════════════════════════════

  private kameraAl(): THREE.Camera | null {
    return (this.anaGosterim?.aktifKamera as THREE.Camera) ?? null;
  }

  private sahneGrubunaErisim(): THREE.Object3D | null {
    const sahne = this.anaGosterim?.sahneler?.[0];
    // FikoSahne kendisi Object3D'yi sarmalar; .ekle/.add ararız
    if (sahne?.anaGosterim?.sahne instanceof THREE.Object3D) return sahne.anaGosterim.sahne;
    if (sahne instanceof THREE.Object3D) return sahne;
    if (sahne?.scene instanceof THREE.Object3D) return sahne.scene;
    return null;
  }

  private temizle(): void {
    this.dolumTemizle();
    this.katilasmaTemizle();
    this.girisYonDurumTemizle();
    this.isiHaritasiTemizle();
    this.etiketleriTemizle();
    // NOT: Yerleştirilen yolluklar KORUNUR (yeniden analiz silmez). Sadece seçim bırakılır.
    try { this.duzenleyici?.sec(null); } catch {}
    this.seciliYolluk.set(null);
    this.seciliBolge.set(null);
  }

  private guvenliLocalStorage(islem: 'get' | 'set', anahtar: string, deger?: string): string | null {
    try {
      if (islem === 'get') return localStorage.getItem(anahtar);
      if (deger != null) localStorage.setItem(anahtar, deger);
    } catch {}
    return null;
  }

  // ───────── Template yardımcıları ─────────
  etiketModuSec(m: EtiketModu): void {
    this.etiketModu.set(m);
    this.guvenliLocalStorage('set', ETIKET_MODU_ANAHTAR, m);
    // Mod 'gizli' iken etiketleri sahneden çıkar; aksi halde (sonuç varsa) yeniden kur
    if (m === 'gizli') {
      this.etiketleriTemizle();
    } else if (this.sahneEtiketObjeleri.length === 0 && this.sonuc()) {
      this.sahneEtiketleriKur(this.sonuc()!.gosterge.bolgeler);
    }
    this.etiketGorunurlukTazele();
  }
  girisYonModuSec(m: GirisYonModu): void {
    this.girisYonModu.set(m);
    this.guvenliLocalStorage('set', GIRIS_YON_MODU_ANAHTAR, m);
    // Mod değişince yarım kalan iki-tık/sürükle durumunu temizle
    this.girisDuzenlemeIptal();
  }
  vurgula(id: string, durum: boolean): void {
    this.hoverBolgeId.set(durum ? id : null);
    this.etiketGorunurlukTazele();
  }
  detayKapat(): void { this.seciliBolge.set(null); this.etiketGorunurlukTazele(); }

  // ───────── Yolluk düzenleme — liste & özellik paneli ─────────
  transformModuSec(m: TransformMod): void {
    this.transformMod.set(m);
    this.duzenleyici?.modAyarla(m);
  }

  /** Döküm/yerçekimi yönünü seç → motoru güncelle → yeniden analiz et.
   *  (Hapsolan hava + besleyici yönü yukari'ya bağlı olduğu için kontrolEt tekrarlanır.) */
  yukariYonSec(s: IYonSecenek): void {
    if (this.yukariYon() === s.deger) return;
    this.yukariYon.set(s.deger);
    this.guvenliLocalStorage('set', YUKARI_YON_ANAHTAR, s.deger);
    try { this.motor?.yukariYonAyarla?.(new THREE.Vector3(...s.yukari)); } catch {}
    this.CD.detectChanges();
    // Önceki analiz varsa yön değişimiyle yeniden hesapla
    if (this.sonuc() && !this.calisiyor()) { this.kontrolEt(); }
  }

  /** Yukarı yönü tersine çevir (parçayı baş aşağı dök) → yeniden analiz */
  yonTersineCevir(): void {
    if (!this.motor) return;
    const v = this.motor.yukariYonTersine();
    const es = this.yonSecenekleri.find(s =>
      Math.abs(s.yukari[0] - v.x) < 0.05 && Math.abs(s.yukari[1] - v.y) < 0.05 && Math.abs(s.yukari[2] - v.z) < 0.05);
    this.yukariYon.set(es ? es.deger : '3D');
    if (es) this.guvenliLocalStorage('set', YUKARI_YON_ANAHTAR, es.deger);
    this.CD.detectChanges();
    if (this.sonuc() && !this.calisiyor()) this.kontrolEt();
  }

  /** 3D'de yön seçme modunu aç/kapat — parçada üst olacak yüzeye tıklanır */
  yon3dToggle(): void {
    this.yon3dSecModu.update(v => !v);
    if (this.yon3dSecModu()) {
      this.tiklamaModu.set(null);
      this.aktifYollukTip.set(null);
      this.nzMessage.info('Parçada ÜST olacak yüzeye tıkla');
    }
    this.CD.detectChanges();
  }

  /** Toolbar etiketi: "⬆ Yerçekimi: −Y" gibi */
  yukariYonEtiket(): string {
    if (this.yukariYon() === '3D') return 'Yerçekimi: özel (3D)';
    const s = this.yonSecenekleri.find(y => y.deger === this.yukariYon());
    if (!s) return '';
    // Metal yukari'nın tersine akar → yerçekimi yönü = -yukari
    const ters = s.deger.startsWith('-') ? '+' + s.deger.slice(1) : '−' + s.deger.slice(1);
    return `Yerçekimi: ${ters}`;
  }

  /** Listeden bir yolluk seç → 3D highlight + gizmo + kameraylaOdakla */
  yollukSec(y: IYollukEleman): void {
    this.duzenleyici?.sec(y.id);
    this.seciliYolluk.set({ ...y });
    this.kameraylaOdakla(y.pozisyon);
    this.CD.detectChanges();
  }

  yollukSecimBirak(): void {
    this.duzenleyici?.sec(null);
    this.seciliYolluk.set(null);
    this.CD.detectChanges();
  }

  /** Yolluk sil (liste 🗑 veya özellik paneli veya Del tuşu) */
  yollukSilTikla(y: IYollukEleman, e?: Event): void {
    e?.stopPropagation();
    if (!this.motor) return;
    const seciliydi = this.seciliYolluk()?.id === y.id;
    try {
      this.motor.yollukSil(y.id);
      if (seciliydi) { this.duzenleyici?.sec(null); this.seciliYolluk.set(null); }
      this.yolluklariYenile();
      this.nzMessage.success('Yolluk silindi.');
    } catch (err: any) {
      this.nzMessage.error('Yolluk silinemedi: ' + (err?.message ?? err));
    }
  }

  /** Hayalet öneriyi kabul et */
  yollukKabulEt(y: IYollukEleman): void {
    if (!this.motor) return;
    try {
      this.motor.yollukKabul(y.id);
      this.yolluklariYenile();
      this.nzMessage.success('Yolluk kabul edildi.');
    } catch (err: any) {
      this.nzMessage.error('Kabul edilemedi: ' + (err?.message ?? err));
    }
  }

  capDegistir(y: IYollukEleman, deger: number): void {
    if (!this.motor) return;
    this.motor.yollukGuncelle(y.id, { capMm: Number(deger) });
    this.yolluklariYenile();
  }

  yukseklikDegistir(y: IYollukEleman, deger: number): void {
    if (!this.motor) return;
    this.motor.yollukGuncelle(y.id, { yukseklikMm: Number(deger) });
    this.yolluklariYenile();
  }

  debiDegistir(y: IYollukEleman, deger: number): void {
    if (!this.motor) return;
    this.motor.yollukGuncelle(y.id, { debiGramSn: Number(deger) });
    this.seciliYollukTazele(y.id);
  }

  akisProfiliDegistir(y: IYollukEleman, profil: AkisProfili): void {
    if (!this.motor) return;
    this.motor.yollukGuncelle(y.id, { akisProfili: profil });
    this.seciliYollukTazele(y.id);
  }

  /** Zamanlı senaryo: girişin açılma anı (s) — "1. giriş t=0, 2. giriş t=15s" */
  acilmaDegistir(y: IYollukEleman, deger: number): void {
    if (!this.motor) return;
    this.motor.yollukGuncelle(y.id, { acilmaSn: Number(deger) });
    this.seciliYollukTazele(y.id);
  }

  /** Zamanlı senaryo: girişin kapanma anı (s) — 0 = hiç kapanmaz */
  kapanmaDegistir(y: IYollukEleman, deger: number): void {
    if (!this.motor) return;
    this.motor.yollukGuncelle(y.id, { kapanmaSn: Number(deger) });
    this.seciliYollukTazele(y.id);
  }

  besleyiciTipiDegistir(y: IYollukEleman, tip: BesleyiciTipi): void {
    if (!this.motor) return;
    this.motor.yollukGuncelle(y.id, { besleyiciTipi: tip });
    this.seciliYollukTazele(y.id);
  }

  sogutucuMalzemeDegistir(y: IYollukEleman, malzeme: SogutucuMalzeme): void {
    if (!this.motor) return;
    this.motor.yollukGuncelle(y.id, { sogutucuMalzeme: malzeme });
    this.seciliYollukTazele(y.id);
  }

  // ───────── Parametrik malzeme editörü (DB üstüne yaz) ─────────
  mlzOverride = signal<Record<string, number>>({});
  mlzAktifMi = computed(() => Object.keys(this.mlzOverride()).length > 0);

  /** Etkin malzeme değeri: kullanıcı override ?? DB alaşımı */
  mlzDeger(alan: string): number {
    const o = this.mlzOverride()[alan];
    if (o != null && isFinite(o)) return o;
    const al: any = getDokumMalzemeDB().alasimAl(this.alasimId());
    const v = al?.[alan];
    return typeof v === 'number' ? v : 0;
  }

  mlzDegistir(alan: string, deger: number): void {
    const v = Number(deger);
    if (!isFinite(v)) return;
    this.mlzOverride.update(o => ({ ...o, [alan]: v }));
    this.motor?.alasimAyarla?.(this.mlzOverride());
  }

  mlzSifirla(): void {
    this.mlzOverride.set({});
    this.motor?.alasimAyarla?.({});
    this.nzMessage.info('Malzeme parametreleri DB değerlerine döndü.');
  }

  // ───────── Senaryo kaydet/yükle (JSON) ─────────
  senaryoKaydet(): void {
    const yolluklar = (this.motor?.yolluklar() ?? []).map(y => ({
      tip: y.tip,
      poz: [y.pozisyon.x, y.pozisyon.y, y.pozisyon.z],
      yon: [y.yon.x, y.yon.y, y.yon.z],
      capMm: y.capMm, yukseklikMm: y.yukseklikMm,
      debiGramSn: y.debiGramSn, akisProfili: y.akisProfili,
      acilmaSn: y.acilmaSn, kapanmaSn: y.kapanmaSn,
      besleyiciTipi: y.besleyiciTipi, sogutucuMalzeme: y.sogutucuMalzeme,
      kabulEdildi: y.kabulEdildi,
    }));
    const senaryo = {
      surum: 1,
      alasimId: this.alasimId(), kumId: this.kumId(),
      malzemeOverride: this.mlzOverride(),
      dokumSicaklikC: this.ayarDokumSicaklik(), kalipSicaklikC: this.ayarKalipSicaklik(),
      cozunurluk: this.voxelCozunurluk(),
      yolluklar,
    };
    const blob = new Blob([JSON.stringify(senaryo, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dokum-senaryo-${this.alasimId()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
    this.nzMessage.success('Senaryo JSON olarak kaydedildi.');
  }

  async senaryoYukle(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const dosya = input.files?.[0];
    input.value = '';
    if (!dosya || !this.motor) return;
    try {
      const s = JSON.parse(await dosya.text());
      if (s.alasimId) this.alasimId.set(s.alasimId);
      if (s.kumId) this.kumId.set(s.kumId);
      this.mlzOverride.set(s.malzemeOverride ?? {});
      this.motor.alasimAyarla?.(this.mlzOverride());
      if (s.dokumSicaklikC != null) this.ayarDokumSicaklik.set(s.dokumSicaklikC);
      if (s.kalipSicaklikC != null) this.ayarKalipSicaklik.set(s.kalipSicaklikC);
      this.prosesAyarUygula();
      if (s.cozunurluk != null) this.voxelCozunurlukDegisti(s.cozunurluk);
      // Yollukları değiştir: mevcutları sil, senaryodakileri kur
      if (Array.isArray(s.yolluklar)) {
        for (const y of this.motor.yolluklar().slice()) this.motor.yollukSil(y.id);
        for (const y of s.yolluklar) {
          const e = this.motor.yollukEkle(
            y.tip,
            new THREE.Vector3(y.poz[0], y.poz[1], y.poz[2]),
            y.yon ? new THREE.Vector3(y.yon[0], y.yon[1], y.yon[2]) : undefined,
          );
          this.motor.yollukGuncelle(e.id, {
            capMm: y.capMm, yukseklikMm: y.yukseklikMm,
            debiGramSn: y.debiGramSn, akisProfili: y.akisProfili,
            acilmaSn: y.acilmaSn, kapanmaSn: y.kapanmaSn,
            besleyiciTipi: y.besleyiciTipi, sogutucuMalzeme: y.sogutucuMalzeme,
          });
        }
        this.yolluklariYenile();
      }
      this.CD.detectChanges();
      this.nzMessage.success(`Senaryo yüklendi: ${s.yolluklar?.length ?? 0} yolluk, ${s.alasimId ?? 'alaşım aynı'}. Dolum/Katılaşma'ya basın.`);
    } catch (e: any) {
      this.nzMessage.error('Senaryo yüklenemedi: ' + (e?.message ?? e));
    }
  }

  // ───────── Dökülebilirlik (castability) skoru + akıllı öneriler ─────────
  /** Tüm tespitleri 0-100 dökülebilirlik skoruna ve önceliklendirilmiş önerilere dönüştür. */
  castabilite(): { puan: number; seviye: string; oneriler: { oncelik: 'yüksek' | 'orta' | 'düşük'; metin: string }[] } | null {
    const s = this.sonuc();
    if (!s) return null;
    let puan = 100;
    const oneriler: { oncelik: 'yüksek' | 'orta' | 'düşük'; metin: string }[] = [];
    const ekle = (oncelik: 'yüksek' | 'orta' | 'düşük', metin: string) => oneriler.push({ oncelik, metin });

    const acik = s.gosterge.bolgeler.filter(b => !b.cozuldu);
    const say = (t: string) => acik.filter(b => b.tip === t).length;
    const sicak = say('sicak_nokta'), ince = say('ince_cidar'), kesit = say('ani_kesit'), hava = say('hapsolan_hava');
    puan -= sicak * 4 + ince * 3 + kesit * 2 + hava * 2;
    if (sicak) ekle('yüksek', `${sicak} sıcak noktaya besleyici veya soğutucu ekleyin (çekme boşluğu önlenir).`);
    if (hava) ekle('orta', `${hava} hapsolan hava bölgesine hava deliği (vent) açın.`);
    if (ince) ekle('orta', `${ince} ince cidar — kesiti kalınlaştırın veya dolum girişini yakına alın.`);
    if (kesit) ekle('düşük', `${kesit} ani kesit geçişine radüs ekleyip yumuşatın.`);

    const d = this.dolumSonuc();
    if (d) {
      if (d.dolmayanSayi > 0) { puan -= 12; ekle('yüksek', 'Eksik dolum (misrun) — döküm sıcaklığını/dolum hızını artırın veya giriş ekleyin.'); }
      if (d.coldShutBolgeler?.length) { puan -= 8; ekle('yüksek', `${d.coldShutBolgeler.length} soğuk birleşme — superheat artırın, daha hızlı dökün, girişi yakınlaştırın.`); }
    }

    const m = this.methodingSonuc();
    if (m && !m.girisHiziUygun) { puan -= 6; ekle('orta', `Giriş hızı ${m.girisHiziMs.toFixed(2)} m/s, limiti (${m.girisHiziLimitMs.toFixed(2)}) aşıyor — giriş kesitini büyütün/debiyi düşürün (türbülans+oksit riski).`); }
    if (m && m.oksitRiski && m.girisHiziUygun) { puan -= 4; ekle('orta', `Yüzey türbülansı (Weber ${m.weberSayisi}>100) — oksit/bifilm riski; girişi büyütün, alttan/sakin dolum yapın.`); }
    if (m && m.verimYuzde < 50) { puan -= 4; ekle('düşük', `Döküm verimi %${m.verimYuzde.toFixed(0)} düşük — besleyici/yolluk boyutlarını optimize edin.`); }

    const k = this.katilasmaSonuc();
    if (k) {
      if (k.cekmeBolgeleri?.length) { puan -= Math.min(20, k.cekmeBolgeleri.length * 2); ekle('yüksek', `${k.cekmeBolgeleri.length} çekme gözenekliliği (Niyama) — besleme yollarını/soğutmayı iyileştirin.`); }
      if (k.makroCekmeBolgeler?.length) { puan -= Math.min(25, k.makroCekmeBolgeler.length * 5); ekle('yüksek', `${k.makroCekmeBolgeler.length} makro çekme boşluğu — beslenmeyen izole bölgeye besleyici/soğutucu ekleyin.`); }
      const y = k.yirtilmaBolgeleri?.length ?? 0;
      if (y) { puan -= Math.min(15, y * 3); ekle('orta', `${y} sıcak yırtılma riski — kalıba esneklik/radüs verin, döküm sıcaklığını düşürün.`); }
      if (k.gazGozeneklilik != null && k.gazGozeneklilik > 0.3) { puan -= 5; ekle('orta', `Gaz gözenekliliği %${(k.gazGozeneklilik * 100).toFixed(0)} — metali gazından arındırın (degassing), döküm sıcaklığını düşürün.`); }
      if (k.distorsiyonIndeks != null && k.distorsiyonIndeks > 0.4) { puan -= 5; ekle('orta', `Distorsiyon/çarpılma riski %${(k.distorsiyonIndeks * 100).toFixed(0)} — soğumayı dengeleyin (soğutucu/besleyici simetrisi), kalıba esneklik verin.`); }
    }

    puan = Math.max(0, Math.min(100, Math.round(puan)));
    const seviye = puan >= 85 ? 'Mükemmel' : puan >= 70 ? 'İyi' : puan >= 55 ? 'Kabul edilebilir' : puan >= 40 ? 'Riskli' : 'Kritik';
    const sira = { 'yüksek': 0, 'orta': 1, 'düşük': 2 };
    oneriler.sort((a, b) => sira[a.oncelik] - sira[b.oncelik]);
    return { puan, seviye, oneriler };
  }

  castabilitePuanRenk(p: number): string {
    return p >= 85 ? '#22c55e' : p >= 70 ? '#84cc16' : p >= 55 ? '#eab308' : p >= 40 ? '#f97316' : '#ef4444';
  }

  // ───────── Proses penceresi (DOE — döküm sıcaklığı taraması) ─────────
  async varyantKarsilastir(): Promise<void> {
    const m = this.methodingSonuc();
    if (!this.motor || !m) { this.nzMessage.warning('Önce "Kontrol Et" yapın.'); return; }
    const tMin = m.dokumSicaklikMinC, tMax = m.dokumSicaklikMaxC;
    const temps = [tMin, (tMin + tMax) / 2, tMax];
    this.varyantCalisiyor.set(true); this.varyantSonuc.set(null); this.CD.detectChanges();
    await new Promise(r => setTimeout(r, 20));
    const sonuclar: { tempC: number; coldShut: number; misrun: number; iyi: boolean }[] = [];
    for (const tc of temps) {
      let s = null;
      try { s = await this.motor.varyantDolum(this.alasimId(), tc); } catch {}
      if (!s) {
        this.varyantCalisiyor.set(false); this.CD.detectChanges();
        this.nzMessage.warning('Önce 💧 Dolum çalıştırın (voxel kafesi gerekli).');
        return;
      }
      sonuclar.push({ tempC: Math.round(tc), coldShut: s.coldShutBolgeler?.length ?? 0, misrun: s.dolmayanSayi, iyi: false });
    }
    // En az toplam kusur = en iyi proses penceresi
    let enIyi = 0, enAz = Infinity;
    sonuclar.forEach((v, i) => { const tk = v.coldShut + v.misrun; if (tk < enAz) { enAz = tk; enIyi = i; } });
    sonuclar[enIyi].iyi = true;
    this.varyantSonuc.set(sonuclar);
    this.varyantCalisiyor.set(false); this.CD.detectChanges();
  }

  // ───────── Kusur raporu ─────────
  raporToggle(): void { this.raporAcik.update(v => !v); }

  /** Tüm tespitleri bölümlere topla (gösterge + methoding + dolum + katılaşma). */
  rapor(): { baslik: string; satirlar: string[] }[] {
    const bols: { baslik: string; satirlar: string[] }[] = [];
    const s = this.sonuc();
    if (!s) return bols;

    bols.push({
      baslik: `Genel — Not ${s.not.not} (${s.not.puan}/100)`,
      satirlar: [s.not.ozet],
    });

    const acik = s.gosterge.bolgeler.filter(b => !b.cozuldu);
    const cozulen = s.gosterge.bolgeler.filter(b => b.cozuldu);
    const gSat: string[] = [];
    for (const b of acik) gSat.push(`⚠ ${b.baslik}: ${b.deger}${b.birim} — ${b.oneri}`);
    for (const b of cozulen) gSat.push(`✓ ${b.baslik} çözüldü${b.cozumMesaji ? ' — ' + b.cozumMesaji : ''}`);
    if (gSat.length === 0) gSat.push('✓ Geometrik sorun yok.');
    bols.push({ baslik: 'Geometri Göstergeleri', satirlar: gSat });

    const m = this.methodingSonuc();
    if (m) {
      const mSat = [
        `Parça: ${m.parcaKutleKg.toFixed(2)} kg, ${m.parcaHacimCm3.toFixed(0)} cm³`,
        `Dolum süresi: ${m.dolumSuresiSn.toFixed(2)} s`,
        `Giriş hızı: ${m.girisHiziMs.toFixed(2)} / ${m.girisHiziLimitMs.toFixed(2)} m/s ${m.girisHiziUygun ? '✓' : '⚠'}`,
        `Weber sayısı: ${m.weberSayisi} ${m.oksitRiski ? '⚠ oksit/türbülans' : '✓'}`,
        `Verim: %${m.verimYuzde.toFixed(0)}`,
      ];
      for (const u of m.uyarilar) mSat.push(`⚠ ${u}`);
      bols.push({ baslik: 'Döküm Hesabı', satirlar: mSat });
    }

    const d = this.dolumSonuc();
    if (d) {
      const dSat: string[] = [`Toplam dolum süresi: ${d.toplamSure.toFixed(2)} s`];
      if (d.dolmayanSayi > 0) dSat.push(`⚠ ${d.dolmayanSayi} voxel dolmadı (misrun/eksik dolum).`);
      if (d.coldShutBolgeler?.length) dSat.push(`⚠ ${d.coldShutBolgeler.length} soğuk birleşme (cold shut) — metal liquidus altında birleşti.`);
      if (d.havaCepleri.length) dSat.push(`⚠ ${d.havaCepleri.length} hapsolan hava cebi.`);
      if (d.sogukBolgeler.length) dSat.push(`⚠ ${d.sogukBolgeler.length} soğuk/geç dolan bölge.`);
      if (dSat.length === 1) dSat.push('✓ Dolum sorunsuz.');
      bols.push({ baslik: 'Dolum Simülasyonu', satirlar: dSat });
    }

    const k = this.katilasmaSonuc();
    if (k) {
      const beslenmemis = k.sicakNoktalar.filter((x: any) => !x.beslendi).length;
      const kSat: string[] = [`Toplam katılaşma süresi: ${k.toplamSure.toFixed(1)} s`];
      if (k.cekmeBolgeleri.length) kSat.push(`⚠ ${k.cekmeBolgeleri.length} çekme gözenekliliği (Niyama < 2).`);
      if (k.makroCekmeBolgeler?.length) kSat.push(`⚠ ${k.makroCekmeBolgeler.length} makro çekme boşluğu (beslenmeyen izole bölge).`);
      if (k.sicakNoktalar.length) kSat.push(`${beslenmemis ? '⚠' : '✓'} ${k.sicakNoktalar.length} sıcak nokta, ${beslenmemis} beslenmemiş.`);
      if (k.yirtilmaBolgeleri.length) kSat.push(`⚠ ${k.yirtilmaBolgeleri.length} sıcak yırtılma riski (Clyne-Davies).`);
      if (k.gazGozeneklilik != null && k.gazGozeneklilik > 0.3) kSat.push(`⚠ Gaz gözeneklilik %${(k.gazGozeneklilik * 100).toFixed(0)} (H çözünürlüğü).`);
      if (k.macaGazBolgeleri?.length) kSat.push(`⚠ ${k.macaGazBolgeleri.length} maça gazı (blowhole) riski — maça havalandırması/soğutucu önerilir.`);
      if (k.gerilmeBolgeler?.length) kSat.push(`⚠ ${k.gerilmeBolgeler.length} artık gerilim bölgesi · tepe ~${(k.maxGerilmeMpa ?? 0).toFixed(0)} MPa · çarpılma ~${(k.distorsiyonMm ?? 0).toFixed(2)} mm.`);
      if (kSat.length === 1) kSat.push('✓ Katılaşma kusuru yok.');
      bols.push({ baslik: 'Katılaşma Simülasyonu', satirlar: kSat });

      // Çekme & besleme (feeding) raporu
      if (k.cekmeBosluk) {
        const cbSat: string[] = [];
        if (k.parcaCekmeCm3 != null && k.parcaCekmeCm3 > 0.01) {
          cbSat.push(`⚠ PARÇADA ${k.parcaCekmeCm3.toFixed(1)} cm³ beslenemeyen çekme boşluğu — besleyici konumu/boyutu yetersiz.`);
        } else {
          cbSat.push(`✓ Parçada çekme boşluğu yok — besleme başarılı (${(k.besleyiciCekmeCm3 ?? 0).toFixed(1)} cm³ besleyicilere alındı).`);
        }
        for (const br of (k.besleyiciRapor ?? [])) {
          cbSat.push(`${br.yeterli ? '✓' : '⚠'} Besleyici (${br.hacimCm3.toFixed(0)} cm³): hacminin %${br.kullanilanYuzde.toFixed(0)}'i pipe olarak tüketildi ${br.yeterli ? '— yeterli' : '— YETERSİZ, büyüt veya ekzotermik gömlek kullan'}.`);
        }
        bols.push({ baslik: 'Çekme & Besleme (Feeding)', satirlar: cbSat });
      }

      if (k.mikroyapi) {
        const mp = k.mikroyapi;
        bols.push({
          baslik: 'Mikroyapı & Mekanik (Al referans)',
          satirlar: [
            `SDAS: ort ${mp.sdasOrtUm.toFixed(0)} µm (${mp.sdasMinUm.toFixed(0)}–${mp.sdasMaxUm.toFixed(0)})`,
            `Çekme dayanımı (UTS): ~${mp.utsMpa.toFixed(0)} MPa`,
            `Akma dayanımı (YS): ~${mp.akmaMpa.toFixed(0)} MPa`,
            `Uzama: ~%${mp.uzamaYuzde.toFixed(1)}`,
            `Sertlik: ~${mp.sertlikHB.toFixed(0)} HB`,
            ...(mp.kolonsalYuzde != null ? [`Tane: %${mp.kolonsalYuzde} kolonsal / %${mp.esEksenliYuzde} eş eksenli`] : []),
          ],
        });
      }
    }

    return bols;
  }

  raporKopyala(): void {
    const metin = this.rapor()
      .map(b => `## ${b.baslik}\n` + b.satirlar.map(s => '  ' + s).join('\n'))
      .join('\n\n');
    try {
      navigator.clipboard?.writeText(metin);
      this.nzMessage.success('Rapor panoya kopyalandı.');
    } catch {
      this.nzMessage.warning('Kopyalama başarısız oldu.');
    }
  }

  yonMetni(y: IYollukEleman): string {
    const v = y.yon;
    if (!v || v.lengthSq() < 1e-8) return '—';
    return `${v.x.toFixed(2)}, ${v.y.toFixed(2)}, ${v.z.toFixed(2)}`;
  }

  gostergeRenk(t: GostergeTipi): string { return GOSTERGE_RENK[t]; }
  gostergeBaslik(t: GostergeTipi): string { return GOSTERGE_BASLIK[t]; }
  bolgeRenk(b: IDokumBolge): string { return b.cozuldu ? '#22c55e' : GOSTERGE_RENK[b.tip]; }
  yollukRenk(t: YollukTipi): string { return YOLLUK_RENK[t]; }
  yollukBaslik(t: YollukTipi): string { return YOLLUK_BASLIK[t]; }
  yollukSimge(t: YollukTipi): string { return this.yollukSimgeler[t]; }
  tipSayisi(t: GostergeTipi): number {
    return this.sonuc()?.gosterge.bolgeler.filter(b => b.tip === t).length ?? 0;
  }
}
